import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { caseApi } from '../services/api';
import { showInAppConfirm } from '../services/inAppDialog';
import { promptCaseCloseAfterServiceReport } from '../utils/serviceReportCaseClose';
import {
  getCurrentDeviceSlaAssignment,
  getDeviceSlaPeriodLabel
} from '../utils/deviceSla';
import {
  getWorkflowStepChecklist,
  normalizeWorkflowJson,
  type WorkflowCapture
} from './WorkflowProtocolEditor';
import { getAuthToken } from '../services/auth';
import { getBaseUrl } from '../services/baseUrl';
import TaskDeviceExpertPanel, {
  type TaskDeviceExpertParentOption
} from './TaskDeviceExpertPanel';
import RepairServiceReportModal from './RepairServiceReportModal';
import type { ServiceReportDocument, ServiceReportRecord } from '../utils/serviceReportTemplate';

const BRAND_TEXT = '#123F43';
const BRAND_MUTED = '#64716F';
const BRAND_SURFACE = '#F2EEE7';
const BRAND_PANEL = '#FBF8F2';
const BRAND_SURFACE_SOFT = '#FBF8F2';
const BRAND_CARD = '#FFFDF9';
const BRAND_BORDER = '#DDD4C8';
const BRAND_TEXT_SOFT = 'rgba(18, 63, 67, 0.08)';
const BRAND_TEXT_SOFT_BORDER = 'rgba(18, 63, 67, 0.18)';
const BRAND_SUCCESS_BG = '#E7F2EC';
const BRAND_SUCCESS_TEXT = '#25624D';
const BRAND_SUCCESS_BORDER = '#9AC6AE';
const BRAND_ACCENT_BG = 'rgba(36, 117, 122, 0.1)';
const BRAND_ACCENT_TEXT = '#0F5561';
const BRAND_WARNING_BG = '#FFF3DF';
const BRAND_WARNING_TEXT = '#8A5A00';
const BRAND_WARNING_BORDER = '#F0C888';
const BRAND_DANGER_BG = '#FDEDEC';
const BRAND_DANGER_TEXT = '#A63B32';

type ImplementationStatus = 'PREPARED' | 'IN_PROGRESS' | 'ON_HOLD' | 'COMPLETED';

type StepAttachmentImage = {
  id?: string;
  label?: string;
  fileUrl?: string;
};

type StepReferenceImageBlock = {
  itemId?: string;
  text?: string;
  imageIds?: string[];
  anchors?: Array<{
    text?: string;
    imageIds?: string[];
  }>;
};

type PartUsage = {
  id: string;
  partNumber: string;
  description: string;
  batchNumber: string;
  quantity: number | null;
};

type PartDraft = {
  partNumber: string;
  description: string;
  batchNumber: string;
  quantity: string;
};

type ExecutionChecklistItem = {
  id: string;
  label: string;
  spec: string | null;
  completed: boolean;
  capture: WorkflowCapture | null;
  captureValue: string;
};

type ExecutionStep = {
  stepKey: string;
  order: number;
  label: string;
  checklist: ExecutionChecklistItem[];
  notes: string;
  partsUsed: PartUsage[];
  collapsed: boolean;
  notApplicable: boolean;
  referenceImages: StepAttachmentImage[];
  referenceImageBlocks: StepReferenceImageBlock[];
};

type StatusDialogState = {
  mode: 'COMPLETE' | 'ON_HOLD' | 'REOPEN';
  reason: string;
  requireReason: boolean;
  title: string;
  message: string;
  confirmLabel: string;
};

type SafetyImplementationWorkspace = {
  serviceReport?: ServiceReportRecord | null;
  [key: string]: any;
};

const normalizeString = (value: unknown) => (typeof value === 'string' ? value.trim() : '');

const formatDateTime = (value: unknown) => {
  const text = normalizeString(value);
  if (!text) return '';
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return text;
  return date.toLocaleString();
};

const parseHoursToMinutes = (value: string): number | null => {
  const numeric = Number(String(value || '').trim());
  if (!Number.isFinite(numeric) || numeric <= 0) return null;
  return Math.round(numeric * 60);
};

const formatHoursFromMinutes = (value: unknown) => {
  const minutes = Number(value);
  if (!Number.isFinite(minutes) || minutes <= 0) return '';
  return String(Math.round((minutes / 60) * 100) / 100);
};

const formatEstimate = (value: unknown) => {
  const minutes = Number(value);
  if (!Number.isFinite(minutes) || minutes <= 0) return '';
  const rounded = Math.round(minutes);
  const hours = Math.floor(rounded / 60);
  const remainder = rounded % 60;
  if (hours > 0 && remainder > 0) return `${hours}h ${remainder}m`;
  if (hours > 0) return `${hours}h`;
  return `${remainder}m`;
};

const createEmptyPartDraft = (): PartDraft => ({
  partNumber: '',
  description: '',
  batchNumber: '',
  quantity: '1'
});

const normalizePartUsage = (value: any, fallbackId: string): PartUsage | null => {
  if (!value || typeof value !== 'object') return null;
  const partNumber = normalizeString(value.partNumber);
  const description = normalizeString(value.description);
  const batchNumber = normalizeString(value.batchNumber);
  const quantity = Number(value.quantity);
  if (!partNumber && !description) return null;
  return {
    id: normalizeString(value.id) || fallbackId,
    partNumber,
    description,
    batchNumber,
    quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : null
  };
};

const normalizePartsUsed = (value: unknown, stepKey: string): PartUsage[] =>
  Array.isArray(value)
    ? value
        .map((entry, index) => normalizePartUsage(entry, `${stepKey}-part-${index + 1}`))
        .filter((entry): entry is PartUsage => Boolean(entry))
    : [];

const normalizeExecutionStatus = (snapshot: any, task: any): ImplementationStatus => {
  const snapshotStatus = normalizeString(snapshot?.status).toUpperCase();
  if (snapshotStatus === 'IN_PROGRESS' || snapshotStatus === 'ON_HOLD' || snapshotStatus === 'COMPLETED') {
    return snapshotStatus as ImplementationStatus;
  }
  if (snapshotStatus === 'PREPARED') return 'PREPARED';
  const taskStatus = normalizeString(task?.status).toUpperCase();
  if (taskStatus === 'IN_PROGRESS' || taskStatus === 'ON_HOLD' || taskStatus === 'COMPLETED') {
    return taskStatus as ImplementationStatus;
  }
  return 'PREPARED';
};

const buildManualWorkflowJson = (caseSummary: any) => ({
  schemaVersion: 2,
  format: 'PROTOCOL_CHECKLIST',
  category: 'SAFETY_UPGRADE',
  title: `Manual safety / upgrade implementation${normalizeString(caseSummary?.caseCode) ? ` - ${normalizeString(caseSummary.caseCode)}` : ''}`,
  description: 'No prepared workflow was loaded for this case. Capture the implementation work manually.',
  steps: [
    {
      stepKey: 'manual-implementation-entry',
      order: 1,
      label: 'Manual implementation entry',
      metadata: {
        layout: 'WORKFLOW',
        checklist: [
          {
            id: 'manual-work',
            label: 'Record the safety / upgrade work carried out for this case.',
            spec: 'Use the notes, parts, and completion controls below to capture the implementation.'
          }
        ]
      }
    }
  ]
});

const buildExecutionSteps = (
  workflowJson: any,
  resultsJson: any
): ExecutionStep[] => {
  const workflow = normalizeWorkflowJson(workflowJson || {});
  const savedSteps = Array.isArray(resultsJson?.steps) ? resultsJson.steps : [];
  const savedByKey = new Map<string, any>();
  savedSteps.forEach((step: any, index: number) => {
    const stepKey = normalizeString(step?.stepKey || step?.id) || `step-${index + 1}`;
    if (!savedByKey.has(stepKey)) savedByKey.set(stepKey, step);
  });

  return (Array.isArray(workflow.steps) ? workflow.steps : []).map((step: any, index: number) => {
    const stepKey = normalizeString(step?.stepKey || step?.id) || `step-${index + 1}`;
    const saved = savedByKey.get(stepKey) || {};
    const savedChecklist = Array.isArray(saved?.checklist) ? saved.checklist : [];
    const savedChecklistById = new Map<string, any>();
    savedChecklist.forEach((item: any, itemIndex: number) => {
      const itemId = normalizeString(item?.id) || `${stepKey}-item-${itemIndex + 1}`;
      if (!savedChecklistById.has(itemId)) savedChecklistById.set(itemId, item);
    });
    return {
      stepKey,
      order: Number.isFinite(Number(step?.order)) ? Number(step.order) : index + 1,
      label: normalizeString(step?.label || step?.title) || `Step ${index + 1}`,
      checklist: getWorkflowStepChecklist(step).map((item: any, itemIndex: number) => {
        const itemId = normalizeString(item?.id) || `${stepKey}-item-${itemIndex + 1}`;
        const savedItem = savedChecklistById.get(itemId) || {};
        return {
          id: itemId,
          label: normalizeString(item?.label || item?.description) || `Checklist item ${itemIndex + 1}`,
          spec: normalizeString(item?.spec) || null,
          completed: Boolean(savedItem?.completed),
          capture: item?.capture && typeof item.capture === 'object' ? item.capture : null,
          captureValue:
            savedItem?.captureValue === null || savedItem?.captureValue === undefined
              ? ''
              : String(savedItem.captureValue)
        };
      }),
      notes: typeof saved?.notes === 'string' ? saved.notes : '',
      partsUsed: normalizePartsUsed(saved?.partsUsed, stepKey),
      collapsed: Boolean(saved?.collapsed),
      notApplicable: Boolean(saved?.notApplicable),
      referenceImages: Array.isArray(step?.metadata?.referenceImages) ? step.metadata.referenceImages : [],
      referenceImageBlocks: Array.isArray(step?.metadata?.referenceImageBlocks)
        ? step.metadata.referenceImageBlocks
        : []
    };
  });
};

const normalizeEvidencePayload = (value: unknown) => {
  return value && typeof value === 'object' ? {} : {};
};

const stepCompletionCounts = (step: ExecutionStep) => {
  const total = step.checklist.length;
  const completed = step.notApplicable ? total : step.checklist.filter(item => item.completed).length;
  return { total, completed };
};

const stepStatusLabel = (step: ExecutionStep) => {
  const counts = stepCompletionCounts(step);
  if (step.notApplicable) return 'Not applicable';
  if (counts.total === 0) {
    return step.notes.trim() || step.partsUsed.length > 0 ? 'In progress' : 'Not started';
  }
  if (counts.completed >= counts.total) return 'Complete';
  if (counts.completed > 0) return 'In progress';
  return 'Not started';
};

const stepStatusHint = (step: ExecutionStep) => {
  const counts = stepCompletionCounts(step);
  if (step.notApplicable) return 'This workflow step is marked not applicable.';
  if (counts.total === 0) {
    return step.notes.trim() || step.partsUsed.length > 0
      ? 'Implementation details have been recorded for this step.'
      : 'Begin workflow steps.';
  }
  if (counts.completed >= counts.total) return 'All workflow items are complete.';
  if (counts.completed > 0) return `${counts.completed} of ${counts.total} workflow items complete.`;
  return 'Begin workflow steps.';
};

const workflowStepSummary = (step: ExecutionStep) => {
  const firstText = step.checklist.map(composeChecklistItemText).find(Boolean) || '';
  if (!firstText) return '';
  return firstText.length > 220 ? `${firstText.slice(0, 217).trimEnd()}...` : firstText;
};

const getStepStatusPillStyle = (step: ExecutionStep): React.CSSProperties => {
  const counts = stepCompletionCounts(step);
  if (step.notApplicable) {
    return {
      background: BRAND_TEXT_SOFT,
      color: BRAND_TEXT,
      border: `1px solid ${BRAND_TEXT_SOFT_BORDER}`
    };
  }
  if (counts.total > 0 && counts.completed >= counts.total) {
    return {
      background: BRAND_SUCCESS_BG,
      color: BRAND_SUCCESS_TEXT,
      border: `1px solid ${BRAND_SUCCESS_BORDER}`
    };
  }
  if (counts.completed > 0 || step.notes.trim() || step.partsUsed.length > 0) {
    return {
      background: BRAND_ACCENT_BG,
      color: BRAND_ACCENT_TEXT,
      border: `1px solid ${BRAND_TEXT_SOFT_BORDER}`
    };
  }
  return {
    background: BRAND_SURFACE,
    color: BRAND_TEXT,
    border: `1px solid ${BRAND_BORDER}`
  };
};

const isExecutionStepComplete = (step: ExecutionStep) => {
  const counts = stepCompletionCounts(step);
  if (step.notApplicable) return true;
  if (counts.total > 0) return counts.completed >= counts.total;
  return Boolean(step.notes.trim() || step.partsUsed.length > 0);
};

const getIncompleteExecutionSteps = (steps: ExecutionStep[]) =>
  steps.filter(step => !isExecutionStepComplete(step));

const capturePlaceholder = (capture: WorkflowCapture | null) => {
  const units = normalizeString(capture?.units);
  return units ? `Enter value (${units})` : 'Enter measured value';
};

const captureHint = (capture: WorkflowCapture | null) => {
  if (!capture) return '';
  const units = normalizeString(capture.units);
  const low = capture.expectedLow;
  const high = capture.expectedHigh;
  const target = capture.target;
  const tolerance = capture.tolerance;
  const parts: string[] = [];
  if (low !== undefined && low !== null && high !== undefined && high !== null) {
    parts.push(`Range ${low}${units ? ` ${units}` : ''} - ${high}${units ? ` ${units}` : ''}`);
  } else if (low !== undefined && low !== null) {
    parts.push(`Minimum ${low}${units ? ` ${units}` : ''}`);
  } else if (high !== undefined && high !== null) {
    parts.push(`Maximum ${high}${units ? ` ${units}` : ''}`);
  } else if (target !== undefined && target !== null && tolerance !== undefined && tolerance !== null) {
    parts.push(`Target ${target}${units ? ` ${units}` : ''} +/- ${tolerance}${units ? ` ${units}` : ''}`);
  }
  if (normalizeString(capture.method)) parts.push(`Method: ${normalizeString(capture.method)}`);
  if (normalizeString(capture.notes)) parts.push(normalizeString(capture.notes));
  return parts.join(' | ');
};

const composeChecklistItemText = (item: ExecutionChecklistItem) => {
  const label = normalizeString(item.label);
  const spec = normalizeString(item.spec);
  if (label && spec) return `${label}: ${spec}`;
  return label || spec || '';
};

const normalizeWhitespace = (value: string): string =>
  String(value || "")
    .replace(/\s+/g, " ")
    .trim();

const findNonOverlappingPhraseRange = (
  source: string,
  phrase: string,
  occupied: Array<{ start: number; end: number }>
): { start: number; end: number } | null => {
  if (!source || !phrase) return null;
  const lowerSource = source.toLowerCase();
  const lowerPhrase = phrase.toLowerCase();
  let index = lowerSource.indexOf(lowerPhrase);
  while (index !== -1) {
    const candidate = { start: index, end: index + phrase.length };
    const overlaps = occupied.some(
      range => !(candidate.end <= range.start || candidate.start >= range.end)
    );
    if (!overlaps) return candidate;
    index = lowerSource.indexOf(lowerPhrase, index + 1);
  }
  return null;
};

const resolveItemReferenceImages = (step: ExecutionStep, item: ExecutionChecklistItem, itemLookupKey: string) => {
  if (step.referenceImages.length === 0 || step.referenceImageBlocks.length === 0) {
    return { block: null, images: [] as StepAttachmentImage[] };
  }

  const itemText = composeChecklistItemText(item);
  if (!itemText) return { block: null, images: [] as StepAttachmentImage[] };

  const matchingBlock = step.referenceImageBlocks.find(block => {
    const blockItemId = String(block?.itemId || '').trim();
    if (!!blockItemId && !!String(itemLookupKey || '').trim() && blockItemId === String(itemLookupKey || '').trim()) return true;
    return normalizeWhitespace(block?.text || '') === itemText;
  });
  if (!matchingBlock) {
    return { block: null, images: [] as StepAttachmentImage[] };
  }

  const imageMap = new Map(step.referenceImages.map(image => [String(image?.id || ''), image]));
  const images = Array.from(
    new Set((Array.isArray(matchingBlock?.imageIds) ? matchingBlock.imageIds : []).map(imageId => String(imageId)))
  )
    .map(imageId => imageMap.get(imageId))
    .filter((image): image is StepAttachmentImage => Boolean(image));
  return { block: matchingBlock, images };
};

function AuthenticatedReferenceImage({
  src,
  alt,
  style,
  placeholderStyle
}: {
  src?: string | null;
  alt: string;
  style: React.CSSProperties;
  placeholderStyle: React.CSSProperties;
}) {
  const [resolvedSrc, setResolvedSrc] = React.useState<string | null>(null);
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    if (!src) {
      setResolvedSrc(null);
      setFailed(false);
      return undefined;
    }

    if (/^(?:data|blob):/i.test(src)) {
      setResolvedSrc(src);
      setFailed(false);
      return undefined;
    }

    let cancelled = false;
    let objectUrl: string | null = null;
    setResolvedSrc(null);
    setFailed(false);

    const fetchImage = async () => {
      const token = getAuthToken();
      const baseUrl = /^(?:https?:|data:|blob:)/i.test(src) ? '' : await getBaseUrl().catch(() => '');
      const requestUrl =
        baseUrl && !/^(?:https?:|data:|blob:)/i.test(src)
          ? `${baseUrl.replace(/\/+$/, '')}${src.startsWith('/') ? src : `/${src}`}`
          : src;
      const response = await fetch(requestUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined
      });
      if (!response.ok) throw new Error('Image unavailable');
      return response.blob();
    };

    fetchImage()
      .then(blob => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setResolvedSrc(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src]);

  if (!src) return <div style={placeholderStyle}>No preview available</div>;
  if (!resolvedSrc && failed) return <div style={placeholderStyle}>Image unavailable</div>;
  if (!resolvedSrc) return <div style={placeholderStyle}>Loading image...</div>;
  return <img src={resolvedSrc} alt={alt} style={style} />;
}

const SafetyUpgradeImplementationTask: React.FC = () => {
  const { caseId = '' } = useParams();
  const navigate = useNavigate();
  const [workspace, setWorkspace] = React.useState<SafetyImplementationWorkspace | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [refreshing, setRefreshing] = React.useState(false);
  const [clearing, setClearing] = React.useState(false);
  const [error, setError] = React.useState('');
  const [steps, setSteps] = React.useState<ExecutionStep[]>([]);
  const [taskStatus, setTaskStatus] = React.useState<ImplementationStatus>('PREPARED');
  const [taskNotes, setTaskNotes] = React.useState('');
  const [statusReason, setStatusReason] = React.useState('');
  const [totalTimeHours, setTotalTimeHours] = React.useState('');
  const [partDrafts, setPartDrafts] = React.useState<Record<string, PartDraft>>({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);
  const [lastSavedAt, setLastSavedAt] = React.useState<string | null>(null);
  const [reportSummary, setReportSummary] = React.useState<string | null>(null);
  const [reportGeneratedAt, setReportGeneratedAt] = React.useState<string | null>(null);
  const [reportLoading, setReportLoading] = React.useState(false);
  const [reportError, setReportError] = React.useState('');
  const [reportIsCustom, setReportIsCustom] = React.useState(false);
  const [reportEditing, setReportEditing] = React.useState(false);
  const [reportDraft, setReportDraft] = React.useState('');
  const [reportSaving, setReportSaving] = React.useState(false);
  const [reportEditError, setReportEditError] = React.useState<string | null>(null);
  const [serviceReportModalOpen, setServiceReportModalOpen] = React.useState(false);
  const [serviceReportDraft, setServiceReportDraft] = React.useState<ServiceReportDocument | null>(null);
  const [serviceReportLoading, setServiceReportLoading] = React.useState(false);
  const [serviceReportSaving, setServiceReportSaving] = React.useState(false);
  const [serviceReportSending, setServiceReportSending] = React.useState(false);
  const [serviceReportError, setServiceReportError] = React.useState<string | null>(null);
  const [serviceReportMode, setServiceReportMode] = React.useState<'create' | 'view'>('create');
  const [statusDialog, setStatusDialog] = React.useState<StatusDialogState | null>(null);
  const [statusDialogError, setStatusDialogError] = React.useState('');
  const [manualMode, setManualMode] = React.useState(false);
  const [manualWorkflowJson, setManualWorkflowJson] = React.useState<any | null>(null);
  const [previewImages, setPreviewImages] = React.useState<StepAttachmentImage[]>([]);
  const [previewImageIndex, setPreviewImageIndex] = React.useState(0);
  const [previewZoom, setPreviewZoom] = React.useState(1);

  const snapshot = workspace?.snapshot || null;
  const caseRecord = workspace?.case || null;
  const access = workspace?.access || {};
  const workspaceReadOnly = access?.canWrite === false;
  const savedServiceReportStatus = String(workspace?.serviceReport?.status || '').trim().toUpperCase();
  const savedServiceReportSent = savedServiceReportStatus === 'SENT';
  const canRebuildServiceReport =
    !workspaceReadOnly &&
    taskStatus === 'COMPLETED' &&
    Boolean(reportSummary?.trim()) &&
    !savedServiceReportSent;
  const assignedLabel = access?.assignedUserName || access?.assignedUserId || null;
  const effectiveWorkflowJson = snapshot?.workflowJson || manualWorkflowJson || null;
  const workflow = React.useMemo(() => normalizeWorkflowJson(effectiveWorkflowJson || {}), [effectiveWorkflowJson]);
  const previewImage =
    previewImages.length > 0 ? previewImages[Math.min(previewImageIndex, previewImages.length - 1)] : null;

  const applyWorkspace = React.useCallback((payload: any) => {
    setWorkspace(payload || null);
    const nextSnapshot = payload?.snapshot || null;
    const workflowJson = nextSnapshot?.workflowJson || null;
    normalizeEvidencePayload(nextSnapshot?.evidenceJson);
    setManualMode(false);
    setManualWorkflowJson(null);
    setSteps(buildExecutionSteps(workflowJson || {}, nextSnapshot?.resultsJson || {}));
    setPartDrafts({});
    setTaskStatus(normalizeExecutionStatus(nextSnapshot, payload?.task));
    setTaskNotes(
      typeof nextSnapshot?.technicianNotes?.overall === 'string'
        ? nextSnapshot.technicianNotes.overall
        : typeof nextSnapshot?.resultsJson?.overallNotes === 'string'
          ? nextSnapshot.resultsJson.overallNotes
          : ''
    );
    setStatusReason(
      typeof nextSnapshot?.resultsJson?.statusReason === 'string' ? nextSnapshot.resultsJson.statusReason : ''
    );
    setTotalTimeHours(formatHoursFromMinutes(nextSnapshot?.resultsJson?.totalTimeMinutes));
    const runMetadata =
      payload?.run && typeof payload.run.metadata === 'object' ? payload.run.metadata : null;
    setReportSummary(
      typeof runMetadata?.reportSummary === 'string' ? runMetadata.reportSummary.trim() : null
    );
    setReportGeneratedAt(
      typeof runMetadata?.reportGeneratedAt === 'string' ? runMetadata.reportGeneratedAt : null
    );
    setReportIsCustom(typeof runMetadata?.reportIsCustom === 'boolean' ? runMetadata.reportIsCustom : false);
    setReportEditing(false);
    setReportDraft('');
    setReportEditError(null);
    setServiceReportError(null);
    setServiceReportMode('create');
    setReportError('');
    setStatusDialog(null);
    setStatusDialogError('');
    setLastSavedAt(nextSnapshot?.updatedAt || null);
    setHasUnsavedChanges(false);
  }, []);

  const loadWorkspace = React.useCallback(async (options: { silent?: boolean } = {}) => {
    if (!caseId) return;
    const silent = options.silent === true;
    if (!silent) setLoading(true);
    setError('');
    try {
      const payload = await caseApi.getSafetyWorkflowWorkspace(caseId);
      applyWorkspace(payload || null);
    } catch (err: any) {
      setError(err?.message || 'Failed to load safety / upgrade implementation.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [applyWorkspace, caseId]);

  React.useEffect(() => {
    void loadWorkspace();
  }, [loadWorkspace]);

  const startManualImplementation = React.useCallback(() => {
    const workflowJson = buildManualWorkflowJson(caseRecord || {});
    setManualMode(true);
    setManualWorkflowJson(workflowJson);
    setSteps(buildExecutionSteps(workflowJson, {}));
    setTaskStatus('PREPARED');
    setTaskNotes('');
    setStatusReason('');
    setTotalTimeHours('');
    setPartDrafts({});
    setReportSummary(null);
    setReportIsCustom(false);
    setReportGeneratedAt(null);
    setReportEditing(false);
    setReportDraft('');
    setReportEditError(null);
    setReportError('');
    setServiceReportModalOpen(false);
    setServiceReportDraft(null);
    setServiceReportError(null);
    setServiceReportMode('create');
    setStatusDialog(null);
    setStatusDialogError('');
    setHasUnsavedChanges(true);
    setError('');
  }, [caseRecord]);

  const reloadSavedImplementation = React.useCallback(async () => {
    if (refreshing || saving) return;
    if (hasUnsavedChanges) {
      const confirmed = await showInAppConfirm(
        'You have unsaved changes. Reload the saved implementation anyway?',
        {
          title: 'Reload saved implementation?',
          confirmLabel: 'Reload',
          tone: 'danger'
        }
      );
      if (!confirmed) return;
    }
    setRefreshing(true);
    setError('');
    try {
      await loadWorkspace({ silent: true });
    } finally {
      setRefreshing(false);
    }
  }, [hasUnsavedChanges, loadWorkspace, refreshing, saving]);

  const markDirty = React.useCallback(() => {
    setHasUnsavedChanges(true);
  }, []);

  const updateStep = React.useCallback((stepKey: string, updater: (step: ExecutionStep) => ExecutionStep) => {
    setSteps(current =>
      current.map(step => (step.stepKey === stepKey ? updater(step) : step))
    );
    markDirty();
  }, [markDirty]);

  const toggleChecklistItem = React.useCallback((stepKey: string, itemId: string) => {
    updateStep(stepKey, step => ({
      ...step,
      checklist: step.checklist.map(item =>
        item.id === itemId ? { ...item, completed: !item.completed } : item
      )
    }));
  }, [updateStep]);

  const updateCaptureValue = React.useCallback((stepKey: string, itemId: string, value: string) => {
    updateStep(stepKey, step => ({
      ...step,
      checklist: step.checklist.map(item =>
        item.id === itemId ? { ...item, captureValue: value } : item
      )
    }));
  }, [updateStep]);

  const updateStepNotes = React.useCallback((stepKey: string, value: string) => {
    updateStep(stepKey, step => ({ ...step, notes: value }));
  }, [updateStep]);

  const toggleStepCollapsed = React.useCallback((stepKey: string) => {
    setSteps(current =>
      current.map(step =>
        step.stepKey === stepKey ? { ...step, collapsed: !step.collapsed } : step
      )
    );
    markDirty();
  }, [markDirty]);

  const toggleStepNotApplicable = React.useCallback((stepKey: string, checked: boolean) => {
    updateStep(stepKey, step => ({ ...step, notApplicable: checked }));
  }, [updateStep]);

  const updatePartDraft = React.useCallback((stepKey: string, field: keyof PartDraft, value: string) => {
    setPartDrafts(current => ({
      ...current,
      [stepKey]: {
        ...(current[stepKey] || createEmptyPartDraft()),
        [field]: value
      }
    }));
  }, []);

  const addPart = React.useCallback((stepKey: string) => {
    const draft = partDrafts[stepKey] || createEmptyPartDraft();
    const normalized = normalizePartUsage(
      {
        id: `${stepKey}-part-${Date.now()}`,
        partNumber: draft.partNumber,
        description: draft.description,
        batchNumber: draft.batchNumber,
        quantity: draft.quantity
      },
      `${stepKey}-part-${Date.now()}`
    );
    if (!normalized) return;
    updateStep(stepKey, step => ({
      ...step,
      partsUsed: [...step.partsUsed, normalized]
    }));
    setPartDrafts(current => ({
      ...current,
      [stepKey]: createEmptyPartDraft()
    }));
  }, [partDrafts, updateStep]);

  const removePart = React.useCallback((stepKey: string, partId: string) => {
    updateStep(stepKey, step => ({
      ...step,
      partsUsed: step.partsUsed.filter(part => part.id !== partId)
    }));
  }, [updateStep]);

  const serializeExecutionSteps = React.useCallback(
    () =>
      steps.map(step => ({
        stepKey: step.stepKey,
        label: step.label,
        notes: step.notes,
        partsUsed: step.partsUsed,
        collapsed: step.collapsed,
        notApplicable: step.notApplicable,
        checklist: step.checklist.map(item => ({
          id: item.id,
          completed: item.completed,
          captureValue: item.captureValue
        }))
      })),
    [steps]
  );

  const buildResultsJson = React.useCallback(
    (statusOverride?: ImplementationStatus, statusReasonOverride?: string) => ({
      ...(snapshot?.resultsJson && typeof snapshot.resultsJson === 'object' ? snapshot.resultsJson : {}),
      totalTimeMinutes: parseHoursToMinutes(totalTimeHours),
      statusReason: statusReasonOverride !== undefined ? statusReasonOverride.trim() : statusReason.trim(),
      overallNotes: taskNotes,
      lastUpdatedAt: new Date().toISOString(),
      status: statusOverride || taskStatus,
      steps: serializeExecutionSteps()
    }),
    [serializeExecutionSteps, snapshot?.resultsJson, statusReason, taskNotes, taskStatus, totalTimeHours]
  );

  const validateSave = React.useCallback(
    async (nextStatus: ImplementationStatus, statusReasonOverride?: string) => {
      const missingCaptureStep = steps.find(step =>
        step.checklist.some(item => item.completed && item.capture && !normalizeString(item.captureValue))
      );
      if (missingCaptureStep) {
        setError(`Enter measured values for completed checklist items in "${missingCaptureStep.label}" before saving.`);
        return false;
      }

      const resolvedReason = statusReasonOverride !== undefined ? statusReasonOverride.trim() : statusReason.trim();
      if (nextStatus === 'ON_HOLD' && !resolvedReason) {
        setError('Add a short status note before placing this implementation on hold.');
        return false;
      }

      if (nextStatus === 'COMPLETED') {
        const incompleteSteps = getIncompleteExecutionSteps(steps);
        if (incompleteSteps.length > 0 && !resolvedReason) {
          setError('Add a completion note explaining why this implementation is being completed with incomplete steps.');
          return false;
        }
      }

      return true;
    },
    [statusReason, steps]
  );

  const saveWorkspace = React.useCallback(
    async (statusOverride?: ImplementationStatus, options: { statusReason?: string } = {}) => {
      if (!caseId || workspaceReadOnly || saving) return false;
      const nextStatus = statusOverride || taskStatus || 'IN_PROGRESS';
      const allowed = await validateSave(nextStatus, options.statusReason);
      if (!allowed) return false;

      setSaving(true);
      setError('');
      try {
        const persistedStatus = nextStatus === 'PREPARED' ? 'IN_PROGRESS' : nextStatus;
        const resolvedStatusReason = options.statusReason !== undefined ? options.statusReason.trim() : statusReason.trim();
        const resultsJson = buildResultsJson(persistedStatus, resolvedStatusReason);
        const evidenceJson = {};
        if (snapshot?.id) {
          await caseApi.updateSafetyWorkflowSnapshot(caseId, snapshot.id, {
            status: persistedStatus,
            technicianNotes: { overall: taskNotes },
            evidenceJson,
            resultsJson
          });
        } else if (manualMode && manualWorkflowJson) {
          await caseApi.saveSafetyWorkflowSnapshot(caseId, {
            title: manualWorkflowJson.title || 'Manual safety / upgrade implementation',
            description: manualWorkflowJson.description || '',
            category: 'SAFETY_UPGRADE',
            sourceType: 'RAW_MANUAL',
            createLibraryDraft: false,
            status: persistedStatus,
            workflowJson: manualWorkflowJson,
            technicianNotes: { overall: taskNotes },
            evidenceJson,
            resultsJson
          });
        } else {
          setError('Prepare a workflow first or start manual implementation for this case.');
          return false;
        }
        setStatusReason(resolvedStatusReason);
        await loadWorkspace({ silent: true });
        return true;
      } catch (err: any) {
        setError(err?.message || 'Failed to save safety / upgrade implementation.');
        return false;
      } finally {
        setSaving(false);
      }
    },
    [
      buildResultsJson,
      caseId,
      loadWorkspace,
      manualMode,
      manualWorkflowJson,
      saving,
      snapshot?.id,
      statusReason,
      taskNotes,
      taskStatus,
      validateSave,
      workspaceReadOnly
    ]
  );

  const applyStatusChange = React.useCallback(
    async (status: ImplementationStatus, reason: string) => {
      await saveWorkspace(status, { statusReason: reason.trim() });
    },
    [saveWorkspace]
  );

  const handleMarkComplete = React.useCallback(() => {
    if (saving || workspaceReadOnly) return;
    const incompleteSteps = getIncompleteExecutionSteps(steps);
    if (incompleteSteps.length > 0) {
      setStatusDialog({
        mode: 'COMPLETE',
        reason: statusReason || '',
        requireReason: true,
        title: 'Complete with incomplete steps?',
        message: `There ${incompleteSteps.length === 1 ? 'is' : 'are'} ${incompleteSteps.length} incomplete workflow step${incompleteSteps.length === 1 ? '' : 's'}. Add a short explanation before forcing completion.`,
        confirmLabel: 'Complete task'
      });
      setStatusDialogError('');
      return;
    }
    void applyStatusChange('COMPLETED', '');
  }, [applyStatusChange, saving, statusReason, steps, workspaceReadOnly]);

  const handlePlaceOnHold = React.useCallback(() => {
    if (saving || workspaceReadOnly) return;
    setStatusDialog({
      mode: 'ON_HOLD',
      reason: statusReason || '',
      requireReason: true,
      title: 'Place implementation on hold?',
      message: 'Add a short note explaining why this implementation is being placed on hold.',
      confirmLabel: 'Place on hold'
    });
    setStatusDialogError('');
  }, [saving, statusReason, workspaceReadOnly]);

  const handleReopen = React.useCallback(() => {
    if (saving || workspaceReadOnly) return;
    setStatusDialog({
      mode: 'REOPEN',
      reason: '',
      requireReason: false,
      title: 'Re-open implementation?',
      message: 'This will return the implementation to in-progress so updates can be recorded.',
      confirmLabel: 'Re-open'
    });
    setStatusDialogError('');
  }, [saving, workspaceReadOnly]);

  const closeStatusDialog = React.useCallback(() => {
    setStatusDialog(null);
    setStatusDialogError('');
  }, []);

  const updateStatusDialogReason = React.useCallback((value: string) => {
    setStatusDialog(current => (current ? { ...current, reason: value } : current));
    if (statusDialogError) setStatusDialogError('');
  }, [statusDialogError]);

  const submitStatusDialog = React.useCallback(async () => {
    if (!statusDialog || workspaceReadOnly) return;
    const trimmed = statusDialog.reason.trim();
    if (statusDialog.requireReason && !trimmed) {
      setStatusDialogError('Please add a short note before continuing.');
      return;
    }
    const targetStatus =
      statusDialog.mode === 'COMPLETE'
        ? 'COMPLETED'
        : statusDialog.mode === 'ON_HOLD'
          ? 'ON_HOLD'
          : 'IN_PROGRESS';
    closeStatusDialog();
    await applyStatusChange(targetStatus, trimmed);
  }, [applyStatusChange, closeStatusDialog, statusDialog, workspaceReadOnly]);

  const handleGenerateSummary = React.useCallback(async () => {
    if (workspaceReadOnly || reportLoading || !caseId) return;
    if (taskStatus !== 'COMPLETED') {
      setReportError('Complete this implementation before generating a summary.');
      return;
    }
    try {
      setReportError('');
      if (hasUnsavedChanges) {
        const saved = await saveWorkspace();
        if (!saved) return;
      }
      setReportLoading(true);
      const response = await caseApi.generateSafetyWorkflowSummary(caseId);
      const summary = typeof response?.summary === 'string' ? response.summary.trim() : '';
      setReportSummary(summary || null);
      setReportGeneratedAt(
        typeof response?.generatedAt === 'string' ? response.generatedAt : new Date().toISOString()
      );
      setReportIsCustom(
        typeof response?.isCustom === 'boolean' ? response.isCustom : false
      );
      if (typeof response?.statusReason === 'string') {
        setStatusReason(response.statusReason);
      }
      await loadWorkspace({ silent: true });
    } catch (err: any) {
      setReportError(err?.message || 'Failed to generate implementation summary.');
    } finally {
      setReportLoading(false);
    }
  }, [
    caseId,
    hasUnsavedChanges,
    loadWorkspace,
    reportLoading,
    saveWorkspace,
    taskStatus,
    workspaceReadOnly
  ]);

  const handleStartEditReport = React.useCallback(() => {
    if (workspaceReadOnly || taskStatus !== 'COMPLETED' || !reportSummary) return;
    setReportDraft(reportSummary);
    setReportEditing(true);
    setReportEditError(null);
  }, [reportSummary, taskStatus, workspaceReadOnly]);

  const handleCancelEditReport = React.useCallback(() => {
    setReportEditing(false);
    setReportDraft('');
    setReportEditError(null);
  }, []);

  const handleSaveEditedReport = React.useCallback(async () => {
    if (workspaceReadOnly || reportSaving || reportLoading || !caseId || taskStatus !== 'COMPLETED') return;
    const trimmed = reportDraft.trim().replace(/\r\n/g, '\n');
    if (!trimmed) {
      setReportEditError('Summary text is required.');
      return;
    }
    try {
      setReportSaving(true);
      setReportEditError(null);
      const response = await caseApi.saveSafetyWorkflowSummary(caseId, trimmed);
      const summary = typeof response?.summary === 'string' ? response.summary.trim() : '';
      setReportSummary(summary || null);
      setReportGeneratedAt(
        typeof response?.generatedAt === 'string' ? response.generatedAt : new Date().toISOString()
      );
      setReportIsCustom(
        typeof response?.isCustom === 'boolean' ? response.isCustom : true
      );
      if (typeof response?.statusReason === 'string') {
        setStatusReason(response.statusReason);
      }
      setReportEditing(false);
      setReportDraft('');
      await loadWorkspace({ silent: true });
    } catch (err: any) {
      setReportEditError(err?.message || 'Failed to save edited summary.');
    } finally {
      setReportSaving(false);
    }
  }, [
    caseId,
    loadWorkspace,
    reportDraft,
    reportLoading,
    reportSaving,
    taskStatus,
    workspaceReadOnly
  ]);

  const closeServiceReportModal = React.useCallback(() => {
    if (serviceReportSaving || serviceReportSending) return;
    setServiceReportModalOpen(false);
    setServiceReportDraft(null);
    setServiceReportMode('create');
    setServiceReportError(null);
  }, [serviceReportSaving, serviceReportSending]);

  const handleViewServiceReport = React.useCallback(() => {
    const existingDocument = workspace?.serviceReport?.document || null;
    if (!existingDocument) {
      setServiceReportError('No saved service report is available yet.');
      return;
    }
    setServiceReportError(null);
    setServiceReportDraft(existingDocument);
    setServiceReportMode('view');
    setServiceReportModalOpen(true);
  }, [workspace]);

  const handleCreateServiceReport = React.useCallback(async () => {
    if (workspaceReadOnly || serviceReportLoading || serviceReportSaving || serviceReportSending) return;
    if (!caseId) return;
    if (taskStatus !== 'COMPLETED') {
      setServiceReportError('Complete the implementation before creating the service report.');
      return;
    }
    if (savedServiceReportSent) {
      setServiceReportError('This service report has already been sent and cannot be regenerated.');
      return;
    }
    if (!reportSummary || !reportSummary.trim()) {
      setServiceReportError('Generate the customer summary before creating the service report.');
      return;
    }
    try {
      setServiceReportLoading(true);
      setServiceReportError(null);
      const response = await caseApi.createSafetyWorkflowServiceReportDraft(caseId);
      const draftDocument =
        response?.document && typeof response.document === 'object'
          ? (response.document as ServiceReportDocument)
          : null;
      if (!draftDocument) {
        throw new Error('Failed to build the service report preview.');
      }
      const summaryText =
        typeof response?.reportSummary === 'string' ? response.reportSummary.trim() : '';
      if (summaryText) {
        setReportSummary(summaryText || null);
      }
      setWorkspace(prev =>
        prev
          ? {
              ...prev,
              serviceReport:
                response?.existingReport && typeof response.existingReport === 'object'
                  ? (response.existingReport as ServiceReportRecord)
                  : prev.serviceReport
            }
          : prev
      );
      setServiceReportDraft(draftDocument);
      setServiceReportMode('create');
      setServiceReportModalOpen(true);
    } catch (err: any) {
      setServiceReportError(err?.message || 'Failed to create the service report preview.');
    } finally {
      setServiceReportLoading(false);
    }
  }, [
    caseId,
    reportSummary,
    savedServiceReportSent,
    serviceReportLoading,
    serviceReportSaving,
    serviceReportSending,
    taskStatus,
    workspaceReadOnly
  ]);

  const handleSaveServiceReport = React.useCallback(async () => {
    if (workspaceReadOnly || serviceReportSaving || serviceReportSending) return;
    if (!caseId || !serviceReportDraft) return;
    try {
      setServiceReportSaving(true);
      setServiceReportError(null);
      const response = await caseApi.saveSafetyWorkflowServiceReport(
        caseId,
        serviceReportDraft as unknown as Record<string, unknown>
      );
      const savedReport =
        response?.serviceReport && typeof response.serviceReport === 'object'
          ? (response.serviceReport as ServiceReportRecord)
          : null;
      if (!savedReport) {
        throw new Error('The service report could not be saved.');
      }
      setWorkspace(prev => (prev ? { ...prev, serviceReport: savedReport } : prev));
      setLastSavedAt(savedReport.savedAt || new Date().toISOString());
      setServiceReportDraft(savedReport.document || serviceReportDraft);
      setServiceReportMode('view');
      setServiceReportModalOpen(true);
    } catch (err: any) {
      setServiceReportError(err?.message || 'Failed to save the service report.');
    } finally {
      setServiceReportSaving(false);
    }
  }, [caseId, serviceReportDraft, serviceReportSaving, serviceReportSending, workspaceReadOnly]);

  const handleSendServiceReport = React.useCallback(async () => {
    if (workspaceReadOnly || serviceReportSaving || serviceReportSending) return;
    if (!caseId || !workspace?.serviceReport?.id) {
      setServiceReportError('Save the service report before sending it to the customer.');
      return;
    }
    try {
      setServiceReportSending(true);
      setServiceReportError(null);
      const response = await caseApi.sendSafetyWorkflowServiceReport(caseId, workspace.serviceReport.id);
      const sentReport =
        response?.serviceReport && typeof response.serviceReport === 'object'
          ? (response.serviceReport as ServiceReportRecord)
          : null;
      if (!sentReport) {
        throw new Error('The service report could not be sent.');
      }
      setWorkspace(prev => (prev ? { ...prev, serviceReport: sentReport } : prev));
      setServiceReportDraft(sentReport.document || serviceReportDraft);
      setServiceReportMode('view');
      await promptCaseCloseAfterServiceReport(caseId, {
        triggerTaskId: workspace?.task?.id || null
      });
    } catch (err: any) {
      setServiceReportError(err?.message || 'Failed to send the service report.');
    } finally {
      setServiceReportSending(false);
    }
  }, [
    caseId,
    serviceReportDraft,
    serviceReportSaving,
    serviceReportSending,
    workspace,
    workspaceReadOnly
  ]);

  const clearProgress = React.useCallback(async () => {
    if (workspaceReadOnly) return;
    const isTaskLocked = taskStatus === 'COMPLETED';
    if (isTaskLocked) {
      setError('Implementation is completed and locked. Re-open it before clearing progress.');
      return;
    }
    if (clearing || saving) return;

    if (hasUnsavedChanges) {
      const confirmed = await showInAppConfirm('You have unsaved changes. Clear progress anyway?', {
        title: 'Clear progress?',
        confirmLabel: 'Clear',
        tone: 'danger'
      });
      if (!confirmed) return;
    }

    setClearing(true);
    setError('');
    try {
      if (snapshot?.id) {
        await caseApi.updateSafetyWorkflowSnapshot(caseId, snapshot.id, {
          status: 'PREPARED',
          evidenceJson: {},
          technicianNotes: { overall: '' },
          resultsJson: {
            ...(snapshot?.resultsJson && typeof snapshot.resultsJson === 'object' ? snapshot.resultsJson : {}),
            totalTimeMinutes: null,
            statusReason: '',
            overallNotes: '',
            lastUpdatedAt: new Date().toISOString(),
            status: 'PREPARED',
            steps: []
          }
        });
        await loadWorkspace({ silent: true });
      } else if (manualMode) {
        startManualImplementation();
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to clear implementation progress.');
    } finally {
      setClearing(false);
    }
  }, [
    caseId,
    clearing,
    hasUnsavedChanges,
    loadWorkspace,
    manualMode,
    saving,
    snapshot?.id,
    snapshot?.resultsJson,
    startManualImplementation,
    taskStatus,
    workspaceReadOnly
  ]);

  const openPreviewImages = React.useCallback((images: StepAttachmentImage[], imageId?: string) => {
    if (!Array.isArray(images) || images.length === 0) return;
    const nextIndex = imageId ? Math.max(0, images.findIndex(image => image?.id === imageId)) : 0;
    setPreviewImages(images);
    setPreviewImageIndex(nextIndex >= 0 ? nextIndex : 0);
    setPreviewZoom(1);
  }, []);

  const closePreviewImages = React.useCallback(() => {
    setPreviewImages([]);
    setPreviewImageIndex(0);
    setPreviewZoom(1);
  }, []);

  const renderChecklistText = React.useCallback(
    (step: ExecutionStep, item: ExecutionChecklistItem, itemLookupKey: string) => {
      const linked = resolveItemReferenceImages(step, item, itemLookupKey);
      const block = linked.block;
      if (!block || !Array.isArray(block.anchors) || block.anchors.length === 0) {
        return (
          <>
            <span style={styles.checkLabel}>{item.label}</span>
            {item.spec ? <span style={styles.checkSpec}> | {item.spec}</span> : null}
          </>
        );
      }

      const itemText = composeChecklistItemText(item);
      const imageMap = new Map(linked.images.map(image => [String(image?.id || ''), image]));
      type AnchorMatch = {
        anchorIndex: number;
        anchorText: string;
        start: number;
        end: number;
        images: StepAttachmentImage[];
      };
      const occupied: Array<{ start: number; end: number }> = [];
      const anchorMatches = block.anchors
        .map((anchor, anchorIndex) => {
          const anchorText = normalizeWhitespace(anchor?.text || '');
          if (!anchorText) return null;
          const range = findNonOverlappingPhraseRange(itemText, anchorText, occupied);
          if (!range) return null;
          const imagesForAnchor = (Array.isArray(anchor?.imageIds) ? anchor.imageIds : [])
            .map(imageId => imageMap.get(String(imageId)))
            .filter((image): image is StepAttachmentImage => Boolean(image));
          occupied.push(range);

          return {
            anchorIndex,
            anchorText,
            start: range.start,
            end: range.end,
            images: imagesForAnchor
          } as AnchorMatch;
        })
        .filter((anchor: AnchorMatch | null): anchor is AnchorMatch => anchor !== null)
        .sort((left, right) => left.start - right.start);

      if (anchorMatches.length === 0) {
        return (
          <>
            <span style={styles.checkLabel}>{item.label}</span>
            {item.spec ? (
              <span style={styles.checkSpec}> | {item.spec}</span>
            ) : null}
          </>
        );
      }
      const parts: React.ReactNode[] = [];
      let cursor = 0;

      anchorMatches.forEach(anchor => {
        if (anchor.start > cursor) {
          parts.push(
            <React.Fragment key={`plain-${anchor.anchorIndex}-${cursor}`}>
              {itemText.slice(cursor, anchor.start)}
            </React.Fragment>
          );
        }

        if (anchor.images.length > 0) {
          parts.push(
            <button
              key={`anchor-${anchor.anchorIndex}-${anchor.start}`}
              type="button"
              onClick={() => openPreviewImages(anchor.images, anchor.images[0]?.id)}
              style={styles.inlineAnchorButton}
            >
              {itemText.slice(anchor.start, anchor.end)}
            </button>
          );
        } else {
          parts.push(
            <React.Fragment key={`text-${anchor.anchorIndex}-${anchor.start}`}>
              {itemText.slice(anchor.start, anchor.end)}
            </React.Fragment>
          );
        }
        cursor = anchor.end;
      });

      if (parts.length === 0) {
        return (
          <>
            <span style={styles.checkLabel}>{item.label}</span>
            {item.spec ? (
              <span style={styles.checkSpec}> | {item.spec}</span>
            ) : null}
          </>
        );
      }
      if (cursor < itemText.length) {
        parts.push(<React.Fragment key={`tail-${cursor}`}>{itemText.slice(cursor)}</React.Fragment>);
      }
      return <span>{parts}</span>;
    },
    [openPreviewImages]
  );

  const workPlanSummary = React.useMemo(() => {
    const preparation = workflow?.preparation && typeof workflow.preparation === 'object' ? workflow.preparation : {};
    const requiredParts = Array.isArray(preparation.requiredParts)
      ? preparation.requiredParts
      : Array.isArray(preparation.parts)
        ? preparation.parts
        : [];
    const specialTools = Array.isArray(preparation.specialTools) ? preparation.specialTools : [];
    const parts = requiredParts
      .map((item: any) => {
        if (typeof item === 'string') return normalizeString(item);
        const number = normalizeString(item?.partNumber || item?.kitNumber || item?.sku);
        const description = normalizeString(item?.description || item?.name);
        return [number, description].filter(Boolean).join(' | ');
      })
      .filter(Boolean);
    const tools = specialTools.map((item: any) => normalizeString(item)).filter(Boolean);
    return {
      estimate: formatEstimate(workflow?.estimatedDurationMinutes || workflow?.estimatedTimeMinutes),
      stepCount: Array.isArray(workflow?.steps) ? workflow.steps.length : 0,
      parts,
      tools
    };
  }, [workflow]);

  const caseLabel = caseRecord?.caseCode || caseRecord?.id || caseId || 'Case';
  const customerLabel = caseRecord?.customer?.company || 'Unknown customer';
  const deviceLabel = caseRecord?.device?.modelNumber
    ? `${caseRecord.device.modelNumber}${caseRecord.device.serialNumber ? ` (Serial ${caseRecord.device.serialNumber})` : ''}`
    : 'Unspecified device';
  const deviceExpertModelId = caseRecord?.device?.modelId || workflow?.deviceModelId || null;
  const deviceExpertOptions = React.useMemo<TaskDeviceExpertParentOption[]>(
    () =>
      steps
        .map((step, stepIndex) => {
          const stepKey = normalizeString(step.stepKey || `workflow-step-${stepIndex + 1}`);
          const contextOptions = step.checklist
            .map((item, itemIndex) => {
              const itemKey = String(item.id || itemIndex + 1).trim();
              const itemText = composeChecklistItemText(item);
              return {
                key: `${stepKey}:${itemKey}`,
                label: normalizeString(item.label) || `Checklist item ${itemIndex + 1}`,
                detail: normalizeString(item.spec),
                prompt: itemText
              };
            })
            .filter(option => option.label || option.prompt);
          return {
            id: stepKey,
            sequence: step.order || stepIndex + 1,
            label: normalizeString(step.label) || `Workflow step ${stepIndex + 1}`,
            contextOptions
          };
        })
        .filter(option => option.label || option.contextOptions.length > 0),
    [steps]
  );
  const locationLabel = caseRecord?.location?.name || caseRecord?.device?.location?.name || 'Unspecified location';
  const contactLabel = caseRecord?.contact?.name || caseRecord?.customer?.company || 'Not provided';
  const activeDeviceSlaAssignment = React.useMemo(
    () => getCurrentDeviceSlaAssignment(caseRecord?.device?.slaAssignments, caseRecord?.openedAt || caseRecord?.createdAt),
    [caseRecord?.createdAt, caseRecord?.device?.slaAssignments, caseRecord?.openedAt]
  );
  const slaHeroSummary = React.useMemo(() => {
    if (activeDeviceSlaAssignment?.slaTemplate) {
      const branding =
        String(
          activeDeviceSlaAssignment.slaTemplate.brandingName ||
            activeDeviceSlaAssignment.slaTemplate.name ||
            'SLA'
        ).trim() || 'SLA';
      const tier = String(activeDeviceSlaAssignment.slaTemplate.tierBrandingName || '').trim();
      const slaType = String(activeDeviceSlaAssignment.slaTemplate.slaType || '').trim();
      const periodLabel = getDeviceSlaPeriodLabel(activeDeviceSlaAssignment);
      const noteParts = [tier, slaType].filter(Boolean);
      if (periodLabel && periodLabel !== 'Not recorded') {
        noteParts.push(periodLabel);
      }
      return {
        value: branding,
        note:
          noteParts.length > 0
            ? noteParts.join(' | ')
            : 'This implementation is linked to an active device SLA.'
      };
    }
    return {
      value: 'No active SLA',
      note: 'This implementation is not currently linked to an active device SLA.'
    };
  }, [activeDeviceSlaAssignment]);
  const statusLabel =
    taskStatus === 'PREPARED'
      ? 'Prepared'
      : taskStatus
          .split('_')
          .map(segment => segment.charAt(0) + segment.slice(1).toLowerCase())
          .join(' ');
  const statusChipStyle = {
    ...styles.statusChip,
    ...(taskStatus === 'COMPLETED'
      ? styles.statusChipCompleted
      : taskStatus === 'ON_HOLD'
        ? styles.statusChipOnHold
        : taskStatus === 'IN_PROGRESS'
          ? styles.statusChipInProgress
          : styles.statusChipPrepared)
  };
  const taskLocked = taskStatus === 'COMPLETED';
  const completedStepCount = React.useMemo(
    () =>
      steps.filter(step => {
        const counts = stepCompletionCounts(step);
        if (step.notApplicable) return true;
        if (counts.total > 0) return counts.completed >= counts.total;
        return Boolean(step.notes.trim() || step.partsUsed.length > 0);
      }).length,
    [steps]
  );
  const totalStepCount = steps.length;
  const suggestedTotalTimeHours = React.useMemo(
    () => formatHoursFromMinutes(workflow?.estimatedDurationMinutes || workflow?.estimatedTimeMinutes),
    [workflow]
  );
  const totalTimeInputNote = 'Enter total onsite time and save implementation to persist it on the case.';
  const functionalTestSummaryLabel = 'No final functional test carried out';
  const functionalTestSummaryNote = 'Optional post-implementation verification only. No final test has been carried out.';
  const topSaveMeta = saving
    ? 'Saving...'
    : hasUnsavedChanges
      ? 'Unsaved changes'
      : lastSavedAt
        ? `Last saved ${formatDateTime(lastSavedAt)}`
        : 'Not saved yet';
  const canSaveImplementation =
    !workspaceReadOnly && !saving && (snapshot || manualMode) && hasUnsavedChanges;
  const canRefreshImplementation = !workspaceReadOnly && !saving && !refreshing && Boolean(snapshot || manualMode);
  const canClearImplementation =
    !workspaceReadOnly && !taskLocked && !saving && !clearing && Boolean(snapshot || manualMode);

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.shell}>
          <div style={styles.section}>
            <div style={styles.muted}>Loading safety / upgrade implementation...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.shell}>
        <div style={styles.topBar}>
          <button type="button" style={styles.backLink} onClick={() => navigate('/tech')}>
            Back to Cases
          </button>
          <div style={styles.topBarActions}>
            <div style={styles.saveMeta}>{topSaveMeta}</div>
            <button
              type="button"
              style={{
                ...styles.primaryButton,
                ...(canSaveImplementation ? null : styles.actionDisabled)
              }}
              onClick={() => void saveWorkspace()}
              disabled={!canSaveImplementation}
            >
              {saving ? 'Saving...' : 'Save implementation'}
            </button>
          </div>
        </div>

        {workspaceReadOnly ? (
          <div style={styles.readOnlyBanner}>
            Only the assigned technician can edit this implementation.
            {assignedLabel ? ` Assigned to ${assignedLabel}.` : ''}
          </div>
        ) : null}

        {error ? <div style={styles.error}>{error}</div> : null}

        <section style={styles.card}>
          <div style={styles.cardHeader}>
            <div style={styles.cardHeaderCopy}>
              <div style={styles.heroTitle}>Safety / Upgrade Implementation</div>
              <div style={styles.heroProblem}>
                {caseRecord?.title || 'Implement the prepared workflow for this case.'}
              </div>
            </div>
            <div style={styles.primaryActions}>
              <button
                type="button"
                style={{
                  ...styles.primaryButton,
                  ...(canRefreshImplementation ? null : styles.actionDisabled)
                }}
                onClick={() => void reloadSavedImplementation()}
                disabled={!canRefreshImplementation}
              >
                {refreshing
                  ? 'Refreshing...'
                  : manualMode
                    ? 'Reload saved implementation'
                    : 'Refresh from workflow'}
              </button>
              <button
                type="button"
                style={{
                  ...styles.secondaryButton,
                  ...(canClearImplementation ? null : styles.actionDisabled)
                }}
                onClick={() => void clearProgress()}
                disabled={!canClearImplementation}
              >
                {clearing ? 'Clearing...' : 'Clear local progress'}
              </button>
            </div>
          </div>
          <div style={styles.grid}>
            <div>
              <div style={styles.fieldLabel}>Case</div>
              <div style={styles.fieldValue}>{caseLabel}</div>
            </div>
            <div>
              <div style={styles.fieldLabel}>Customer</div>
              <div style={styles.fieldValue}>{customerLabel}</div>
            </div>
            <div>
              <div style={styles.fieldLabel}>Device</div>
              <div style={styles.fieldValue}>{deviceLabel}</div>
            </div>
            <div>
              <div style={styles.fieldLabel}>Location</div>
              <div style={styles.fieldValue}>{locationLabel}</div>
            </div>
            <div>
              <div style={styles.fieldLabel}>Contact</div>
              <div style={styles.fieldValue}>{contactLabel}</div>
            </div>
            <div>
              <div style={styles.fieldLabel}>SLA</div>
              <div style={styles.fieldValue}>{slaHeroSummary.value}</div>
              <div style={styles.statusMetaSubnote}>{slaHeroSummary.note}</div>
            </div>
          </div>
        </section>

        {!snapshot && !manualMode ? (
          <section style={styles.section}>
            <div style={styles.sectionHeader}>
              <div>
                <div style={styles.sectionTitle}>No prepared workflow loaded</div>
                <div style={styles.sectionSub}>
                  Use the Workflow Preparation task from the case workspace if you need a prepared workflow first, or
                  start a manual implementation entry here.
                </div>
              </div>
            </div>
            <div style={styles.contextStrip}>
              If you continue manually here, the case gets its own persistent workflow snapshot without creating an
              admin-approved reusable workflow.
            </div>
            <div style={styles.primaryActions}>
              <button
                type="button"
                style={styles.secondaryButton}
                onClick={() => navigate('/tech')}
              >
                Back to Cases
              </button>
              <button
                type="button"
                style={styles.primaryButton}
                onClick={startManualImplementation}
                disabled={workspaceReadOnly}
              >
                Start manual implementation
              </button>
            </div>
          </section>
        ) : (
          <>
            <section style={styles.section}>
              <div style={styles.statusHeader}>
                <div style={styles.statusLeft}>
                  <div>
                    <div style={styles.fieldLabel}>Status</div>
                    <div style={styles.statusChipRow}>
                      <span style={statusChipStyle}>{statusLabel}</span>
                    </div>
                    {statusReason ? (
                      <div style={styles.statusNote}>Status note: {statusReason}</div>
                    ) : null}
                  </div>
                <div style={styles.statusMetaRow}>
                  <div style={styles.statusMetaItem}>
                    <div style={styles.fieldLabel}>Workflow steps completed</div>
                    <div style={styles.fieldValue}>
                      {completedStepCount} of {totalStepCount}
                    </div>
                  </div>
                  <div style={styles.statusMetaItem}>
                    <div style={styles.fieldLabel}>Final functional test</div>
                    <div style={styles.fieldValue}>{functionalTestSummaryLabel}</div>
                    <div style={styles.statusMetaSubnote}>{functionalTestSummaryNote}</div>
                  </div>
                  <div style={{ ...styles.statusMetaItem, maxWidth: '14rem' }}>
                    <div style={styles.fieldLabel}>Total time (hours)</div>
                      <input
                        style={{
                          ...styles.textInput,
                          ...(taskLocked || workspaceReadOnly ? styles.textInputDisabled : {}),
                          background: '#ffffff'
                        }}
                        value={totalTimeHours}
                        onChange={event => {
                          setTotalTimeHours(event.target.value);
                          markDirty();
                        }}
                        placeholder={suggestedTotalTimeHours || 'e.g. 2.5'}
                        inputMode="decimal"
                        disabled={taskLocked || workspaceReadOnly}
                      />
                      <div style={styles.statusMetaSubnote}>{totalTimeInputNote}</div>
                    </div>
                  </div>
                </div>
                <div style={styles.statusRight}>
                  <div style={styles.fieldLabel}>Actions</div>
                  <div style={styles.primaryActions}>
                    <button
                      type="button"
                      style={{
                        ...styles.statusActionComplete,
                        ...((taskStatus === 'COMPLETED' || saving || workspaceReadOnly)
                          ? styles.statusActionDisabled
                          : {})
                      }}
                      onClick={handleMarkComplete}
                      disabled={taskStatus === 'COMPLETED' || saving || workspaceReadOnly}
                    >
                      Mark complete
                    </button>
                    <button
                      type="button"
                      style={{
                        ...styles.statusActionHold,
                        ...((taskStatus === 'ON_HOLD' || taskStatus === 'COMPLETED' || saving || workspaceReadOnly)
                          ? styles.statusActionDisabled
                          : {})
                      }}
                      onClick={handlePlaceOnHold}
                      disabled={taskStatus === 'ON_HOLD' || taskStatus === 'COMPLETED' || saving || workspaceReadOnly}
                    >
                      Place on hold
                    </button>
                    <button
                      type="button"
                      style={{
                        ...styles.statusActionReopen,
                        ...((taskStatus === 'IN_PROGRESS' || taskStatus === 'PREPARED' || saving || workspaceReadOnly)
                          ? styles.statusActionDisabled
                          : {})
                      }}
                      onClick={handleReopen}
                      disabled={taskStatus === 'IN_PROGRESS' || taskStatus === 'PREPARED' || saving || workspaceReadOnly}
                    >
                      Re-open
                    </button>
                  </div>
                  <button
                    type="button"
                    style={{
                      ...styles.reportButton,
                      ...((taskStatus !== 'COMPLETED' || reportLoading || workspaceReadOnly) ? styles.reportButtonDisabled : {})
                    }}
                    onClick={() => void handleGenerateSummary()}
                    disabled={taskStatus !== 'COMPLETED' || reportLoading || workspaceReadOnly}
                  >
                    {reportLoading ? 'Generating...' : reportSummary ? 'Regenerate summary' : 'Generate summary'}
                  </button>
                </div>
              </div>
              {reportError ? <div style={styles.error}>{reportError}</div> : null}
              <section style={styles.section}>
                <div style={styles.reportHeader}>
                  <div>
                    <div style={styles.reportTitle}>Customer summary</div>
                    {reportGeneratedAt ? (
                      <div style={styles.reportMeta}>
                        {reportIsCustom ? 'Last updated' : 'Last generated'} {formatDateTime(reportGeneratedAt)}
                      </div>
                    ) : null}
                  </div>
                  <div style={styles.reportActions}>
                    {workspace?.serviceReport ? (
                      <button type="button" style={styles.reportEditButton} onClick={handleViewServiceReport}>
                        View service report
                      </button>
                    ) : null}
                    <button
                      type="button"
                      style={{
                        ...styles.reportEditButton,
                      ...(taskStatus !== 'COMPLETED' ||
                      reportSaving ||
                      serviceReportLoading ||
                      serviceReportSaving ||
                      serviceReportSending ||
                      !reportSummary?.trim() ||
                      savedServiceReportSent ||
                      workspaceReadOnly
                        ? styles.reportEditButtonDisabled
                        : {})
                    }}
                      onClick={handleCreateServiceReport}
                      disabled={
                        taskStatus !== 'COMPLETED' ||
                        reportSaving ||
                        serviceReportLoading ||
                        serviceReportSaving ||
                        serviceReportSending ||
                        !reportSummary?.trim() ||
                        savedServiceReportSent ||
                        workspaceReadOnly
                      }
                    >
                      {serviceReportLoading
                        ? 'Building service report...'
                        : workspace?.serviceReport?.document
                        ? 'Regenerate service report'
                        : 'Create service report'}
                    </button>
                    {!reportEditing ? (
                      <button
                        type="button"
                        style={{
                          ...styles.reportEditButton,
                          ...(reportSaving || taskStatus !== 'COMPLETED' || !reportSummary?.trim() || workspaceReadOnly
                            ? styles.reportEditButtonDisabled
                            : {})
                        }}
                        onClick={handleStartEditReport}
                        disabled={reportSaving || taskStatus !== 'COMPLETED' || !reportSummary?.trim() || workspaceReadOnly}
                      >
                        Edit summary
                      </button>
                    ) : null}
                  </div>
                </div>
                {workspace?.serviceReport?.savedAt ? (
                  <div style={styles.reportMeta}>
                    Service report saved {formatDateTime(workspace.serviceReport.savedAt) || workspace.serviceReport.savedAt}
                    {workspace.serviceReport.templateName ? ` using ${workspace.serviceReport.templateName}` : ''}
                  </div>
                ) : null}
                {workspace?.serviceReport ? (
                  <div style={styles.reportStatusWrap}>
                    <div
                      style={{
                        ...styles.reportStatusChip,
                        ...(String(workspace.serviceReport.status || '').trim().toUpperCase() === 'SENT'
                          ? styles.reportStatusChipSent
                          : styles.reportStatusChipPending)
                      }}
                    >
                      {String(workspace.serviceReport.status || '').trim().toUpperCase() === 'SENT'
                        ? 'Report Sent'
                        : 'Not Sent'}
                    </div>
                    <div style={styles.reportMeta}>
                      {String(workspace.serviceReport.status || '').trim().toUpperCase() === 'SENT'
                        ? workspace.serviceReport.sentAt
                          ? `Sent ${formatDateTime(workspace.serviceReport.sentAt) || workspace.serviceReport.sentAt}`
                          : 'Sent date unavailable'
                        : 'Visible to the customer only after you send the report.'}
                    </div>
                  </div>
                ) : null}
                {serviceReportError ? <div style={styles.reportEditError}>{serviceReportError}</div> : null}
                {reportEditing ? (
                  <>
                    <textarea
                      style={styles.reportSummaryTextarea}
                      value={reportDraft}
                      onChange={event => {
                        setReportDraft(event.target.value);
                        if (reportEditError) setReportEditError(null);
                      }}
                      placeholder="Customer-facing summary..."
                      disabled={reportSaving || workspaceReadOnly}
                    />
                    {reportEditError ? <div style={styles.reportEditError}>{reportEditError}</div> : null}
                    <div style={styles.reportEditFooter}>
                      <button
                        type="button"
                        style={{
                          ...styles.reportEditSecondary,
                          ...(reportSaving || workspaceReadOnly ? styles.reportEditButtonDisabled : {})
                        }}
                        onClick={handleCancelEditReport}
                        disabled={reportSaving || workspaceReadOnly}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        style={{
                          ...styles.reportEditPrimary,
                          ...(reportSaving || !reportDraft.trim() || workspaceReadOnly
                            ? styles.reportEditButtonDisabled
                            : {})
                        }}
                        onClick={handleSaveEditedReport}
                        disabled={reportSaving || !reportDraft.trim() || workspaceReadOnly}
                      >
                        {reportSaving ? 'Saving...' : 'Save changes'}
                      </button>
                    </div>
                  </>
                ) : reportSummary ? (
                  <div style={styles.reportBody}>{reportSummary}</div>
                ) : (
                  <div style={styles.muted}>Generate a customer summary to begin.</div>
                )}
              </section>
              <div style={{ width: '100%' }}>
                <div style={styles.fieldLabel}>Task notes</div>
                <textarea
                  style={{ ...styles.textarea, background: '#ffffff' }}
                  value={taskNotes}
                  onChange={event => {
                    setTaskNotes(event.target.value);
                    markDirty();
                  }}
                  placeholder="Overall notes, risks, follow-ups..."
                  disabled={workspaceReadOnly || taskStatus === 'COMPLETED'}
                />
              </div>
            </section>

            <section style={styles.section}>
              <div style={styles.sectionTitle}>Work Plan Summary</div>
              <div style={styles.workPlanStats}>
                <div style={styles.workPlanMetric}>
                  <div style={styles.workPlanMetricValue}>{workPlanSummary.estimate || '-'}</div>
                  <div style={styles.metricLabel}>Estimated onsite time</div>
                </div>
                <div style={styles.workPlanMetric}>
                  <div style={styles.workPlanMetricValue}>{workPlanSummary.stepCount}</div>
                  <div style={styles.metricLabel}>Workflow steps</div>
                </div>
                <div style={styles.workPlanMetric}>
                  <div style={styles.workPlanMetricValue}>{workPlanSummary.parts.length}</div>
                  <div style={styles.metricLabel}>Required parts</div>
                </div>
              </div>
              <div style={styles.prepGrid}>
                <PrepList title="Required special tools" items={workPlanSummary.tools} empty="No special tools listed." />
                <PrepList title="Required parts" items={workPlanSummary.parts} empty="No required parts listed." />
              </div>
            </section>

            <TaskDeviceExpertPanel
              modelId={deviceExpertModelId}
              parentOptions={deviceExpertOptions}
              taskLabel="safety / upgrade implementation"
              parentLabel="Workflow step"
              contextLabel="Checklist item"
              subtitle="Ask about a selected implementation step for contextual help, or ask an open device question."
              noParentLabel="No specific workflow step selected"
              noContextLabel="No checklist item selected"
              selectParentFirstLabel="Select a workflow step first"
              questionPlaceholder="Ask about this implementation, or leave blank to get more detail on the selected checklist item."
              contextHint="Choosing a checklist item adds that implementation context. With no step selected, your question is treated as an open Device Expert ask."
              unavailableText="This safety / upgrade implementation has no device-model context yet, so Device Expert is unavailable."
              emptyContextText="No workflow steps are available for Device Expert context yet."
            />

            <section style={styles.section}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  <div style={{ fontSize: '1.25rem', fontWeight: 600, color: BRAND_TEXT }}>
                    Implementation execution steps
                  </div>
                  <span style={styles.muted}>
                    {manualMode
                      ? 'No prepared workflow is attached. Use manual implementation entry for notes, parts, and completion.'
                      : 'Track workflow step completion and implementation progress, then add parts and notes as needed.'}
                  </span>
                </div>
              </div>
              {steps.length === 0 ? (
                <div style={styles.emptyState}>
                  <div style={styles.emptyTitle}>No execution steps available</div>
                  <div style={styles.emptyCopy}>Prepare a workflow first or start a manual implementation entry.</div>
                </div>
              ) : (
                <div style={styles.stepsColumn}>
                 {steps.map((step, stepIndex) => {
                    const draft = partDrafts[step.stepKey] || createEmptyPartDraft();
                    const canAddPart = normalizeString(draft.partNumber) || normalizeString(draft.description);
                    const stepSummary = workflowStepSummary(step);
                    const stepHint = stepStatusHint(step);
                    const canSaveStep = !workspaceReadOnly && !taskLocked && !saving && hasUnsavedChanges;
                    const stepComplete = isExecutionStepComplete(step);
                    return (
                      <section
                        key={step.stepKey}
                        style={{
                          ...styles.stepCard,
                          ...(stepComplete ? styles.stepCardComplete : {})
                        }}
                      >
                        <div style={styles.stepHeader}>
                          <div style={styles.stepHeaderCopy}>
                            <div style={styles.stepTitle}>
                              {step.order}. {step.label}
                            </div>
                            {stepSummary ? <div style={styles.stepSummary}>{stepSummary}</div> : null}
                            <div style={styles.stepStatusRow}>
                              <div style={styles.stepStatusRowLeft}>
                                <span
                                  style={{
                                    ...styles.stepStatusPill,
                                    ...getStepStatusPillStyle(step)
                                  }}
                                >
                                  {stepStatusLabel(step)}
                                </span>
                                <span style={styles.stepStatusHint}>{stepHint}</span>
                              </div>
                            </div>
                          </div>
                          <div style={styles.stepHeaderActions}>
                            <div style={styles.stepHeaderActionsTop}>
                              <button
                                type="button"
                                style={{
                                  ...styles.primaryButton,
                                  minHeight: '2.5rem',
                                  padding: '0.5rem 1rem',
                                  ...(!canSaveStep ? styles.actionDisabled : {})
                                }}
                                onClick={() => void saveWorkspace()}
                                disabled={!canSaveStep}
                              >
                                {saving ? 'Saving step...' : canSaveStep ? 'Save step' : 'Step saved'}
                              </button>
                              <button
                                type="button"
                                style={styles.stepCollapseToggle}
                                onClick={() => toggleStepCollapsed(step.stepKey)}
                              >
                                {step.collapsed ? 'Expand' : 'Collapse'}
                              </button>
                            </div>
                          </div>
                        </div>

                        {!step.collapsed ? (
                          <>
                            <div style={styles.stepChecklistSection}>
                              <div style={styles.stepChecklistHeader}>
                                <strong>Workflow steps</strong>
                                <div style={styles.stepChecklistHeaderActions}>
                                  <button
                                    type="button"
                                    style={styles.stepChecklistToggle}
                                    onClick={() => toggleStepCollapsed(step.stepKey)}
                                  >
                                    Hide
                                  </button>
                                </div>
                              </div>
                              <div style={styles.stepChecklistBody}>
                                {step.checklist.length === 0 ? (
                                  <div style={styles.muted}>No workflow steps recorded for this implementation step.</div>
                                ) : (
                                  <>
                                    <div style={styles.stepChecklistHint}>
                                      Complete the prepared workflow items in order and record required measured values before saving the step.
                                    </div>
                                     {step.checklist.map((item, itemIndex) => (
                                      <label key={item.id} style={styles.stepChecklistItem}>
                                        <input
                                          type="checkbox"
                                          checked={item.completed}
                                          onChange={() => toggleChecklistItem(step.stepKey, item.id)}
                                          disabled={workspaceReadOnly || taskStatus === 'COMPLETED'}
                                          style={{ marginTop: '0.3rem' }}
                                        />
                                        <div style={styles.stepChecklistContent}>
                                         <span style={styles.checkboxCopy}>
                                          {renderChecklistText(step, item, `${step.stepKey || stepIndex}:${item?.id || itemIndex + 1}`)}
                                         </span>
                                          {item.capture ? (
                                            <div style={styles.captureRow}>
                                              <div style={styles.captureInputGroup}>
                                                <div style={styles.captureLabel}>Measured value</div>
                                                <input
                                                  style={styles.captureInput}
                                                  value={item.captureValue}
                                                  onChange={event => updateCaptureValue(step.stepKey, item.id, event.target.value)}
                                                  placeholder={capturePlaceholder(item.capture)}
                                                  disabled={workspaceReadOnly || taskStatus === 'COMPLETED'}
                                                />
                                                {captureHint(item.capture) ? (
                                                  <div style={styles.captureHint}>{captureHint(item.capture)}</div>
                                                ) : null}
                                              </div>
                                            </div>
                                          ) : null}
                                        </div>
                                      </label>
                                    ))}
                                  </>
                                )}
                              </div>
                            </div>

                            <div style={styles.partsCard}>
                              <div style={styles.cardTitle}>Parts used</div>
                              {step.partsUsed.length === 0 ? (
                                <div style={styles.muted}>No parts recorded.</div>
                              ) : (
                                <div style={styles.partsTable}>
                                  <div style={{ ...styles.partsRow, ...styles.partsHeaderRow }}>
                                    <span>Part number</span>
                                    <span>Description</span>
                                    <span>Batch / lot</span>
                                    <span>Quantity</span>
                                    <span />
                                  </div>
                                  {step.partsUsed.map(part => (
                                    <div key={part.id} style={styles.partsRow}>
                                      <span style={styles.partCellValue}>{part.partNumber || '-'}</span>
                                      <span style={styles.partCellValue}>{part.description || '-'}</span>
                                      <span style={styles.partCellValue}>{part.batchNumber || '-'}</span>
                                      <span style={styles.partCellValue}>{part.quantity === null ? '-' : part.quantity}</span>
                                      <div style={styles.partActions}>
                                        <button
                                          type="button"
                                          style={styles.partRemoveButton}
                                          onClick={() => removePart(step.stepKey, part.id)}
                                          disabled={workspaceReadOnly || taskStatus === 'COMPLETED'}
                                        >
                                          Remove
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                              <div style={styles.partDraftLabel}>Add new part</div>
                              <div style={styles.partDraftHint}>
                                Record the part number, description, batch or lot number, and quantity used during this workflow step.
                              </div>
                              <div style={styles.partDraftGrid}>
                                <input
                                  style={styles.partCellInput}
                                  value={draft.partNumber}
                                  onChange={event => updatePartDraft(step.stepKey, 'partNumber', event.target.value)}
                                  placeholder="Part number"
                                  disabled={workspaceReadOnly || taskStatus === 'COMPLETED'}
                                />
                                <input
                                  style={styles.partCellInput}
                                  value={draft.description}
                                  onChange={event => updatePartDraft(step.stepKey, 'description', event.target.value)}
                                  placeholder="Part description"
                                  disabled={workspaceReadOnly || taskStatus === 'COMPLETED'}
                                />
                                <input
                                  style={styles.partCellInput}
                                  value={draft.batchNumber}
                                  onChange={event => updatePartDraft(step.stepKey, 'batchNumber', event.target.value)}
                                  placeholder="Batch / lot number"
                                  disabled={workspaceReadOnly || taskStatus === 'COMPLETED'}
                                />
                                <input
                                  style={{ ...styles.partCellInput, maxWidth: '6rem' }}
                                  value={draft.quantity}
                                  onChange={event => updatePartDraft(step.stepKey, 'quantity', event.target.value)}
                                  placeholder="Qty"
                                  inputMode="decimal"
                                  disabled={workspaceReadOnly || taskStatus === 'COMPLETED'}
                                />
                                <button
                                  type="button"
                                  style={styles.partAddButton}
                                  onClick={() => addPart(step.stepKey)}
                                  disabled={!canAddPart || workspaceReadOnly || taskStatus === 'COMPLETED'}
                                >
                                  Add part
                                </button>
                              </div>
                            </div>

                            <div style={styles.notesCard}>
                              <div style={styles.notesHeader}>
                                <div style={styles.cardTitle}>Technician notes</div>
                                <label style={styles.stepNotesCheckbox}>
                                  <input
                                    type="checkbox"
                                    checked={step.notApplicable}
                                    onChange={event => toggleStepNotApplicable(step.stepKey, event.target.checked)}
                                    disabled={workspaceReadOnly || taskStatus === 'COMPLETED'}
                                  />
                                  <span>Mark this step not applicable</span>
                                </label>
                              </div>
                              <textarea
                                style={{ ...styles.textarea, background: '#ffffff' }}
                                value={step.notes}
                                onChange={event => updateStepNotes(step.stepKey, event.target.value)}
                                placeholder="Add any observations for this workflow step"
                                disabled={workspaceReadOnly || taskStatus === 'COMPLETED'}
                              />
                            </div>
                          </>
                        ) : null}
                      </section>
                    );
                  })}
                </div>
              )}
            </section>
          </>
        )}
      </div>

      {statusDialog ? (
        <div style={styles.dialogBackdrop}>
          <div style={styles.dialogCard}>
            <div>
              <div style={styles.dialogTitle}>{statusDialog.title}</div>
              <div style={styles.dialogMessage}>{statusDialog.message}</div>
            </div>
            <textarea
              style={styles.dialogTextarea}
              value={statusDialog.reason}
              onChange={event => updateStatusDialogReason(event.target.value)}
              placeholder={statusDialog.requireReason ? 'Required explanation...' : 'Optional note...'}
              disabled={saving || workspaceReadOnly}
            />
            {statusDialogError ? <div style={styles.dialogError}>{statusDialogError}</div> : null}
            <div style={styles.dialogActions}>
              <button
                type="button"
                style={styles.secondaryButton}
                onClick={closeStatusDialog}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                style={{
                  ...styles.primaryButton,
                  ...(statusDialog.mode === 'COMPLETE' ? styles.dangerButton : {})
                }}
                onClick={() => void submitStatusDialog()}
                disabled={saving || workspaceReadOnly}
              >
                {statusDialog.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {previewImage ? (
        <div style={styles.previewBackdrop} onClick={closePreviewImages}>
          <div style={styles.previewCard} onClick={event => event.stopPropagation()}>
            <div style={styles.previewHeader}>
              <div>
                <div style={styles.previewTitle}>{previewImage.label || 'Reference image'}</div>
                <div style={styles.previewCount}>
                  {previewImageIndex + 1} / {previewImages.length}
                </div>
              </div>
              <button type="button" style={styles.previewCloseButton} onClick={closePreviewImages}>
                X
              </button>
            </div>
            <div style={styles.previewToolbar}>
              <button
                type="button"
                style={styles.previewToolbarButton}
                onClick={() => {
                  setPreviewImageIndex(current => Math.max(0, current - 1));
                  setPreviewZoom(1);
                }}
                disabled={previewImageIndex <= 0}
              >
                Previous
              </button>
              <button
                type="button"
                style={styles.previewToolbarButton}
                onClick={() => {
                  setPreviewImageIndex(current => Math.min(previewImages.length - 1, current + 1));
                  setPreviewZoom(1);
                }}
                disabled={previewImageIndex >= previewImages.length - 1}
              >
                Next
              </button>
              <button
                type="button"
                style={styles.previewToolbarButton}
                onClick={() => setPreviewZoom(current => Math.max(1, Number((current - 0.25).toFixed(2))))}
              >
                -
              </button>
              <input
                type="range"
                min={1}
                max={4}
                step={0.1}
                value={previewZoom}
                onChange={event => setPreviewZoom(Number(event.target.value))}
                style={styles.previewRange}
              />
              <button
                type="button"
                style={styles.previewToolbarButton}
                onClick={() => setPreviewZoom(current => Math.min(4, Number((current + 0.25).toFixed(2))))}
              >
                +
              </button>
              <button type="button" style={styles.previewToolbarButton} onClick={() => setPreviewZoom(1)}>
                100%
              </button>
              <span style={styles.previewZoomLabel}>{Math.round(previewZoom * 100)}%</span>
            </div>
            <div style={styles.previewViewport}>
              <AuthenticatedReferenceImage
                src={previewImage.fileUrl || null}
                alt={previewImage.label || 'Reference image'}
                style={{
                  ...styles.previewImage,
                  maxWidth: previewZoom <= 1 ? '100%' : `${previewZoom * 100}%`,
                  maxHeight: previewZoom <= 1 ? '70vh' : 'none'
                }}
                placeholderStyle={styles.previewImagePlaceholder}
              />
            </div>
          </div>
        </div>
      ) : null}

      <RepairServiceReportModal
        open={serviceReportModalOpen}
        document={serviceReportDraft}
        saving={serviceReportSaving}
        regenerating={serviceReportLoading}
        sending={serviceReportSending}
        error={serviceReportError}
        mode={serviceReportMode}
        subtitle={
          serviceReportMode === 'view'
            ? 'Locked saved service report snapshot'
            : 'Preview the template-filled report generated from this safety / upgrade summary.'
        }
        hint={
          serviceReportMode === 'view'
            ? savedServiceReportSent
              ? 'This service report has already been sent to the customer.'
              : 'This saved service report has not been sent yet. Regenerate it after summary or template changes, then save the refreshed report before sending.'
            : 'Review the generated service report before saving it to the case.'
        }
        canRegenerate={serviceReportMode === 'view' && canRebuildServiceReport}
        regenerateLabel="Regenerate service report"
        onRegenerate={handleCreateServiceReport}
        saveLabel={workspace?.serviceReport?.document ? 'Save refreshed report' : 'Create report'}
        canSend={
          serviceReportMode === 'view' &&
          !workspaceReadOnly &&
          taskStatus === 'COMPLETED' &&
          Boolean(workspace?.serviceReport?.id) &&
          !savedServiceReportSent
        }
        sendLabel="Send to customer"
        onClose={closeServiceReportModal}
        onSave={handleSaveServiceReport}
        onSend={handleSendServiceReport}
      />
    </div>
  );
};

const PrepList: React.FC<{ title: string; items: string[]; empty: string }> = ({ title, items, empty }) => (
  <div style={styles.prepList}>
    <div style={styles.formLabel}>{title}</div>
    {items.length > 0 ? (
      <div style={styles.prepItems}>
        {items.map(item => (
          <span key={item} style={styles.prepChip}>{item}</span>
        ))}
      </div>
    ) : (
      <div style={styles.muted}>{empty}</div>
    )}
  </div>
);

const styles: Record<string, React.CSSProperties> = {
  page: {
    background: BRAND_SURFACE,
    minHeight: '100vh',
    padding: '2.5rem clamp(1rem, 4vw, 3rem) 4rem',
    color: BRAND_TEXT
  },
  shell: {
    maxWidth: '1600px',
    margin: '0 auto',
    width: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: '1.75rem'
  },
  topBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '1rem',
    flexWrap: 'wrap'
  },
  topBarActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    flexWrap: 'wrap'
  },
  backLink: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '2.7rem',
    padding: '0.65rem 1rem',
    cursor: 'pointer',
    color: BRAND_TEXT,
    textDecoration: 'none',
    fontWeight: 700,
    backgroundColor: BRAND_CARD,
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '0.8rem',
    boxShadow: '0 1px 2px rgba(18, 63, 67, 0.08)'
  },
  saveMeta: {
    color: BRAND_MUTED,
    fontSize: '0.875rem'
  },
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '1rem',
    flexWrap: 'wrap'
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    flexWrap: 'wrap',
    justifyContent: 'flex-end'
  },
  backButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '2.7rem',
    padding: '0.65rem 1rem',
    cursor: 'pointer',
    color: BRAND_TEXT,
    fontWeight: 700,
    backgroundColor: BRAND_CARD,
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '0.8rem',
    boxShadow: '0 1px 2px rgba(18, 63, 67, 0.08)'
  },
  button: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '2.7rem',
    padding: '0.65rem 1rem',
    borderRadius: '0.8rem',
    border: `1px solid ${BRAND_BORDER}`,
    fontWeight: 800,
    cursor: 'pointer',
    boxShadow: '0 10px 20px -18px rgba(18, 63, 67, 0.45)'
  },
  primaryButton: {
    background: BRAND_TEXT,
    border: `1px solid ${BRAND_TEXT}`,
    color: '#ffffff',
    borderRadius: '0.8rem',
    padding: '0.65rem 1.4rem',
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: '0 10px 20px -16px rgba(18, 63, 67, 0.48)',
    minHeight: '2.7rem'
  },
  secondaryButton: {
    background: BRAND_CARD,
    border: `1px solid ${BRAND_BORDER}`,
    color: BRAND_TEXT,
    borderRadius: '0.8rem',
    padding: '0.65rem 1.4rem',
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: '0 1px 2px rgba(18, 63, 67, 0.08)',
    minHeight: '2.7rem'
  },
  warningButton: {
    background: BRAND_WARNING_BG,
    color: BRAND_WARNING_TEXT,
    borderColor: BRAND_WARNING_BORDER
  },
  actionDisabled: {
    opacity: 0.65,
    cursor: 'not-allowed'
  },
  savedAt: {
    color: BRAND_MUTED,
    fontSize: '0.875rem'
  },
  card: {
    background: BRAND_CARD,
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '1rem',
    padding: '1.75rem',
    boxShadow: '0 20px 40px -28px rgba(18, 63, 67, 0.22)',
    display: 'flex',
    flexDirection: 'column',
    gap: '1.25rem'
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '1rem',
    flexWrap: 'wrap'
  },
  cardHeaderCopy: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.6rem'
  },
  heroTitle: {
    fontSize: '1.75rem',
    fontWeight: 700,
    color: BRAND_TEXT
  },
  heroProblem: {
    fontWeight: 600,
    color: BRAND_TEXT,
    maxWidth: '52rem',
    lineHeight: 1.4
  },
  grid: {
    display: 'grid',
    gap: '1rem',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))'
  },
  caseHeaderCard: {
    background: BRAND_CARD,
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '1rem',
    padding: '1.75rem',
    boxShadow: '0 20px 40px -28px rgba(18, 63, 67, 0.22)',
    display: 'flex',
    flexDirection: 'column',
    gap: '1.25rem'
  },
  caseHeaderTop: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '1rem',
    flexWrap: 'wrap'
  },
  caseHeaderCopy: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.55rem',
    minWidth: 0
  },
  caseHeroTitle: {
    margin: 0,
    fontSize: '1.75rem',
    fontWeight: 800,
    color: BRAND_TEXT
  },
  caseHeroProblem: {
    fontWeight: 600,
    color: BRAND_TEXT,
    maxWidth: '60rem',
    lineHeight: 1.45
  },
  caseHeaderGrid: {
    display: 'grid',
    gap: '1rem',
    gridTemplateColumns: 'repeat(auto-fit, minmax(12rem, 1fr))'
  },
  caseHeaderField: {
    minWidth: 0
  },
  caseHeaderFieldLabel: {
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: BRAND_MUTED,
    fontWeight: 700
  },
  caseHeaderFieldValue: {
    color: BRAND_TEXT,
    fontWeight: 700,
    overflowWrap: 'anywhere'
  },
  casePersistenceNote: {
    border: `1px solid ${BRAND_TEXT_SOFT_BORDER}`,
    background: BRAND_ACCENT_BG,
    borderRadius: '0.85rem',
    padding: '0.85rem 1rem',
    color: BRAND_TEXT,
    lineHeight: 1.45,
    fontWeight: 600
  },
  section: {
    background: BRAND_CARD,
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '1rem',
    padding: '1.75rem',
    boxShadow: '0 15px 35px -30px rgba(18, 63, 67, 0.2)',
    display: 'flex',
    flexDirection: 'column',
    gap: '1.25rem'
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '1rem',
    alignItems: 'flex-start',
    flexWrap: 'wrap'
  },
  sectionTitle: {
    fontSize: '1.1rem',
    fontWeight: 800,
    color: BRAND_TEXT
  },
  sectionSub: {
    marginTop: '0.3rem',
    color: BRAND_MUTED,
    lineHeight: 1.4
  },
  contextStrip: {
    border: `1px solid ${BRAND_TEXT_SOFT_BORDER}`,
    background: BRAND_PANEL,
    borderRadius: '0.85rem',
    padding: '0.9rem 1rem',
    lineHeight: 1.5,
    color: BRAND_TEXT
  },
  statusChip: {
    borderRadius: '0.6rem',
    padding: '0.35rem 0.75rem',
    fontWeight: 800,
    fontSize: '0.8rem',
    border: `1px solid ${BRAND_TEXT_SOFT_BORDER}`
  },
  statusChipPrepared: {
    background: BRAND_TEXT_SOFT,
    color: BRAND_TEXT
  },
  statusChipInProgress: {
    background: BRAND_ACCENT_BG,
    color: BRAND_ACCENT_TEXT
  },
  statusChipOnHold: {
    background: BRAND_WARNING_BG,
    color: BRAND_WARNING_TEXT,
    borderColor: BRAND_WARNING_BORDER
  },
  statusChipCompleted: {
    background: BRAND_SUCCESS_BG,
    color: BRAND_SUCCESS_TEXT,
    borderColor: BRAND_SUCCESS_BORDER
  },
  statusHeader: {
    display: 'flex',
    gap: '1.75rem',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap'
  },
  statusLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    minWidth: '16rem',
    flex: '1 1 18rem'
  },
  statusChipRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  },
  statusNote: {
    color: BRAND_MUTED,
    fontSize: '0.85rem',
    marginTop: '0.45rem'
  },
  statusMetaRow: {
    display: 'flex',
    gap: '1.25rem',
    flexWrap: 'wrap'
  },
  statusMetaItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.35rem',
    minWidth: '10rem'
  },
  statusMetaSubnote: {
    color: BRAND_MUTED,
    fontSize: '0.8rem',
    lineHeight: 1.4
  },
  statusRight: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    alignItems: 'flex-end',
    flex: '0 0 auto'
  },
  statusActionComplete: {
    background: BRAND_SUCCESS_TEXT,
    border: `1px solid ${BRAND_SUCCESS_TEXT}`,
    color: '#ffffff',
    borderRadius: '0.8rem',
    padding: '0.65rem 1.8rem',
    fontWeight: 700,
    fontSize: '0.9rem',
    cursor: 'pointer',
    minHeight: '2.75rem'
  },
  statusActionHold: {
    background: BRAND_WARNING_BG,
    border: `1px solid ${BRAND_WARNING_BORDER}`,
    color: BRAND_WARNING_TEXT,
    borderRadius: '0.8rem',
    padding: '0.65rem 1.8rem',
    fontWeight: 700,
    fontSize: '0.9rem',
    cursor: 'pointer',
    minHeight: '2.75rem'
  },
  statusActionReopen: {
    background: BRAND_TEXT_SOFT,
    border: `1px solid ${BRAND_TEXT_SOFT_BORDER}`,
    color: BRAND_TEXT,
    borderRadius: '0.8rem',
    padding: '0.65rem 1.8rem',
    fontWeight: 700,
    fontSize: '0.9rem',
    cursor: 'pointer',
    minHeight: '2.75rem'
  },
  statusActionDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed'
  },
  reportButton: {
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: BRAND_TEXT,
    border: `1px solid ${BRAND_TEXT}`,
    color: '#ffffff',
    borderRadius: '0.8rem',
    padding: '0.75rem 2.4rem',
    fontWeight: 700,
    fontSize: '0.95rem',
    cursor: 'pointer',
    minWidth: '16rem',
    minHeight: '2.85rem',
    boxShadow: '0 10px 20px -16px rgba(18, 63, 67, 0.48)'
  },
  reportButtonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed'
  },
  reportSummaryCard: {
    border: `1px solid ${BRAND_TEXT_SOFT_BORDER}`,
    borderRadius: '0.9rem',
    background: BRAND_PANEL,
    padding: '1rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.65rem'
  },
  reportSummaryHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '1rem',
    flexWrap: 'wrap',
    alignItems: 'center'
  },
  reportSummaryMeta: {
    color: BRAND_MUTED,
    fontSize: '0.85rem',
    fontWeight: 600
  },
  reportSummaryText: {
    whiteSpace: 'pre-wrap',
    lineHeight: 1.55,
    color: BRAND_TEXT
  },
  reportHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '1rem',
    flexWrap: 'wrap'
  },
  reportTitle: {
    fontSize: '1.1rem',
    fontWeight: 800,
    color: BRAND_TEXT
  },
  reportMeta: {
    color: BRAND_MUTED,
    fontSize: '0.85rem'
  },
  reportActions: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '0.75rem',
    justifyContent: 'flex-end'
  },
  reportEditButton: {
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '0.75rem',
    background: '#ffffff',
    color: BRAND_TEXT,
    padding: '0.58rem 1rem',
    fontWeight: 700,
    fontSize: '0.85rem',
    cursor: 'pointer'
  },
  reportEditButtonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed'
  },
  reportStatusWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.7rem',
    flexWrap: 'wrap',
    marginBottom: '0.75rem'
  },
  reportStatusChip: {
    borderRadius: '999px',
    padding: '0.3rem 0.7rem',
    fontSize: '0.78rem',
    fontWeight: 700
  },
  reportStatusChipSent: {
    background: BRAND_SUCCESS_BG,
    color: BRAND_SUCCESS_TEXT,
    border: `1px solid ${BRAND_SUCCESS_BORDER}`
  },
  reportStatusChipPending: {
    background: BRAND_WARNING_BG,
    color: BRAND_WARNING_TEXT,
    border: `1px solid ${BRAND_WARNING_BORDER}`
  },
  reportEditError: {
    color: BRAND_DANGER_TEXT,
    fontWeight: 700
  },
  reportEditFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.75rem',
    flexWrap: 'wrap'
  },
  reportEditPrimary: {
    border: `1px solid ${BRAND_TEXT}`,
    background: BRAND_TEXT,
    color: '#ffffff',
    borderRadius: '0.75rem',
    padding: '0.6rem 1rem',
    fontWeight: 700,
    fontSize: '0.88rem',
    cursor: 'pointer'
  },
  reportEditSecondary: {
    border: `1px solid ${BRAND_BORDER}`,
    background: '#ffffff',
    color: BRAND_TEXT,
    borderRadius: '0.75rem',
    padding: '0.6rem 1rem',
    fontWeight: 700,
    fontSize: '0.88rem',
    cursor: 'pointer'
  },
  reportBody: {
    whiteSpace: 'pre-wrap',
    lineHeight: 1.55,
    color: BRAND_TEXT
  },
  reportSummaryTextarea: {
    width: '100%',
    minHeight: 'clamp(22rem, 42vh, 34rem)',
    borderRadius: '0.9rem',
    border: `1px solid ${BRAND_BORDER}`,
    background: '#FFFFFF',
    padding: '1rem 1.1rem',
    fontSize: '0.98rem',
    fontFamily: 'inherit',
    lineHeight: 1.55,
    color: BRAND_TEXT,
    resize: 'vertical',
    boxSizing: 'border-box'
  },
  statusGrid: {
    display: 'grid',
    gap: '1rem',
    gridTemplateColumns: 'repeat(auto-fit, minmax(14rem, 1fr))'
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.45rem'
  },
  fieldLabel: {
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: BRAND_MUTED,
    fontWeight: 700
  },
  fieldValue: {
    color: BRAND_TEXT,
    fontWeight: 600
  },
  formLabel: {
    fontSize: '0.78rem',
    fontWeight: 800,
    color: BRAND_MUTED,
    textTransform: 'uppercase',
    letterSpacing: '0.08em'
  },
  input: {
    width: '100%',
    minHeight: '2.8rem',
    borderRadius: '0.8rem',
    border: `1px solid ${BRAND_BORDER}`,
    background: '#FFFFFF',
    padding: '0.72rem 0.9rem',
    fontSize: '0.98rem',
    color: BRAND_TEXT,
    boxSizing: 'border-box'
  },
  textInput: {
    borderRadius: '0.8rem',
    border: `1px solid ${BRAND_BORDER}`,
    padding: '0.6rem 0.85rem',
    width: '100%',
    fontSize: '0.95rem',
    background: BRAND_CARD,
    color: BRAND_TEXT,
    boxSizing: 'border-box'
  },
  textInputDisabled: {
    background: BRAND_PANEL,
    color: BRAND_MUTED,
    cursor: 'not-allowed'
  },
  textarea: {
    width: '100%',
    minHeight: '7rem',
    borderRadius: '0.9rem',
    border: `1px solid ${BRAND_BORDER}`,
    background: '#FFFFFF',
    padding: '0.85rem 0.95rem',
    fontSize: '0.98rem',
    color: BRAND_TEXT,
    resize: 'vertical',
    boxSizing: 'border-box'
  },
  readOnlyBanner: {
    borderRadius: '0.85rem',
    border: `1px solid ${BRAND_WARNING_BORDER}`,
    background: BRAND_WARNING_BG,
    color: BRAND_WARNING_TEXT,
    padding: '0.95rem 1rem',
    fontWeight: 700
  },
  error: {
    borderRadius: '0.85rem',
    border: `1px solid #F4C8C5`,
    background: BRAND_DANGER_BG,
    color: BRAND_DANGER_TEXT,
    padding: '0.95rem 1rem',
    fontWeight: 700
  },
  primaryActions: {
    display: 'flex',
    gap: '0.75rem',
    flexWrap: 'wrap'
  },
  workPlanStats: {
    display: 'grid',
    gap: '1rem',
    gridTemplateColumns: 'repeat(auto-fit, minmax(12rem, 1fr))'
  },
  workPlanMetric: {
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '0.9rem',
    background: '#FFFFFF',
    padding: '1rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.35rem',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center'
  },
  workPlanMetricValue: {
    fontSize: '2rem',
    fontWeight: 800,
    color: BRAND_TEXT
  },
  metricLabel: {
    color: BRAND_MUTED,
    fontSize: '0.9rem'
  },
  prepGrid: {
    display: 'grid',
    gap: '1rem',
    gridTemplateColumns: 'repeat(auto-fit, minmax(18rem, 1fr))'
  },
  prepList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.6rem'
  },
  prepItems: {
    display: 'flex',
    gap: '0.5rem',
    flexWrap: 'wrap'
  },
  prepChip: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '0.38rem 0.7rem',
    borderRadius: '999px',
    border: `1px solid ${BRAND_TEXT_SOFT_BORDER}`,
    background: BRAND_ACCENT_BG,
    color: BRAND_TEXT,
    fontWeight: 700,
    fontSize: '0.88rem'
  },
  stepsColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem'
  },
  stepCard: {
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '1rem',
    background: BRAND_SURFACE_SOFT,
    padding: '1.25rem 1.5rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem'
  },
  stepCardComplete: {
    background: BRAND_SUCCESS_BG,
    border: `1px solid ${BRAND_SUCCESS_BORDER}`
  },
  stepHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '1rem',
    flexWrap: 'wrap'
  },
  stepHeaderCopy: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.45rem',
    flex: 1
  },
  stepTitle: {
    fontWeight: 700,
    fontSize: '1rem',
    color: BRAND_TEXT
  },
  stepSummary: {
    color: BRAND_MUTED,
    fontSize: '0.9rem',
    lineHeight: 1.45
  },
  stepStatusRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.75rem',
    flexWrap: 'wrap'
  },
  stepStatusRowLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    flexWrap: 'wrap'
  },
  stepStatusPill: {
    borderRadius: '9999px',
    padding: '0.25rem 0.75rem',
    fontSize: '0.75rem',
    fontWeight: 700
  },
  stepStatusHint: {
    color: BRAND_MUTED,
    fontSize: '0.8rem'
  },
  stepHeaderActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    flexWrap: 'wrap',
    justifyContent: 'flex-end'
  },
  stepHeaderActionsTop: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    flexWrap: 'wrap',
    justifyContent: 'flex-end'
  },
  stepCollapseToggle: {
    background: 'none',
    border: 'none',
    color: BRAND_TEXT,
    cursor: 'pointer',
    fontWeight: 600,
    padding: 0
  },
  stepChecklistSection: {
    background: '#FFFEFB',
    borderRadius: '0.75rem',
    border: `1px solid ${BRAND_BORDER}`
  },
  stepChecklistHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.85rem 1rem',
    background: BRAND_SURFACE,
    borderRadius: '0.75rem 0.75rem 0 0'
  },
  stepChecklistHeaderActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.85rem'
  },
  stepChecklistToggle: {
    background: 'none',
    border: 'none',
    color: BRAND_TEXT,
    cursor: 'pointer',
    fontWeight: 600,
    padding: 0
  },
  stepChecklistBody: {
    background: '#ffffff',
    padding: '0.9rem 1.1rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    borderRadius: '0 0 0.75rem 0.75rem'
  },
  stepChecklistHint: {
    fontSize: '0.78rem',
    color: BRAND_MUTED,
    background: BRAND_SURFACE_SOFT,
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '0.5rem',
    padding: '0.5rem 0.65rem',
    marginBottom: '0.2rem'
  },
  stepChecklistItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.55rem'
  },
  stepChecklistContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.35rem'
  },
  checkboxCopy: {
    lineHeight: 1.5,
    color: BRAND_TEXT
  },
  checkLabel: {
    fontWeight: 800,
    color: BRAND_TEXT
  },
  checkSpec: {
    color: '#425b7b'
  },
  inlineAnchorButton: {
    border: 'none',
    background: 'transparent',
    padding: 0,
    margin: 0,
    color: '#0E5874',
    textDecoration: 'underline',
    cursor: 'pointer',
    font: 'inherit'
  },
  captureBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.4rem'
  },
  captureRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.35rem'
  },
  captureInputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
    maxWidth: '16rem'
  },
  captureLabel: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: BRAND_TEXT
  },
  captureInput: {
    width: '100%',
    borderRadius: '0.6rem',
    border: `1px solid ${BRAND_BORDER}`,
    background: BRAND_CARD,
    padding: '0.45rem 0.6rem',
    fontSize: '0.9rem',
    color: BRAND_TEXT,
    boxSizing: 'border-box'
  },
  captureHint: {
    color: BRAND_MUTED,
    fontSize: '0.75rem'
  },
  partsCard: {
    background: BRAND_SURFACE_SOFT,
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '0.85rem',
    padding: '1rem 1.1rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.85rem'
  },
  notesCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem'
  },
  stepDivider: {
    borderTop: `1px dashed ${BRAND_BORDER}`
  },
  cardTitle: {
    fontWeight: 800,
    color: BRAND_TEXT
  },
  notesHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '0.5rem'
  },
  stepNotesCheckbox: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.45rem',
    color: BRAND_MUTED,
    fontSize: '0.9rem'
  },
  partsTable: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.65rem'
  },
  partsRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.4fr) minmax(0, 1fr) minmax(100px, 0.6fr) auto',
    gap: '0.55rem',
    alignItems: 'center'
  },
  partsHeaderRow: {
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: BRAND_MUTED,
    fontWeight: 700
  },
  partCellValue: {
    display: 'flex',
    alignItems: 'center',
    minHeight: '2.5rem',
    borderRadius: '0.65rem',
    border: `1px solid ${BRAND_BORDER}`,
    padding: '0.55rem 0.65rem',
    background: BRAND_CARD,
    fontSize: '0.9rem',
    color: BRAND_TEXT,
    overflowWrap: 'anywhere'
  },
  partActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.5rem',
    flexWrap: 'wrap'
  },
  partDraftGrid: {
    display: 'grid',
    gap: '0.55rem',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.4fr) minmax(0, 1fr) minmax(100px, 0.6fr) auto',
    alignItems: 'center'
  },
  partCellInput: {
    borderRadius: '0.65rem',
    border: `1px solid ${BRAND_BORDER}`,
    padding: '0.55rem 0.65rem',
    width: '100%',
    fontSize: '0.9rem',
    background: '#ffffff',
    color: BRAND_TEXT,
    boxSizing: 'border-box'
  },
  partDraftLabel: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: BRAND_TEXT
  },
  partDraftHint: {
    fontSize: '0.8rem',
    color: BRAND_MUTED
  },
  partAddButton: {
    background: BRAND_TEXT,
    border: `1px solid ${BRAND_TEXT}`,
    color: '#ffffff',
    borderRadius: '0.8rem',
    padding: '0.65rem 1rem',
    fontWeight: 600,
    cursor: 'pointer',
    alignSelf: 'flex-start'
  },
  partRemoveButton: {
    border: `1px solid #E4BBB7`,
    background: BRAND_DANGER_BG,
    color: BRAND_DANGER_TEXT,
    borderRadius: '9999px',
    padding: '0.35rem 0.85rem',
    fontWeight: 600,
    cursor: 'pointer'
  },
  dangerButton: {
    background: BRAND_DANGER_TEXT,
    border: `1px solid ${BRAND_DANGER_TEXT}`,
    color: '#ffffff'
  },
  dialogBackdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(18, 63, 67, 0.55)',
    zIndex: 1200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1.5rem'
  },
  dialogCard: {
    width: 'min(560px, 100%)',
    background: BRAND_CARD,
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '1rem',
    padding: '1.4rem',
    boxShadow: '0 22px 55px -28px rgba(18, 63, 67, 0.65)',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem'
  },
  dialogTitle: {
    color: BRAND_TEXT,
    fontWeight: 800,
    fontSize: '1.2rem',
    marginBottom: '0.35rem'
  },
  dialogMessage: {
    color: BRAND_MUTED,
    lineHeight: 1.45
  },
  dialogTextarea: {
    width: '100%',
    minHeight: '7rem',
    borderRadius: '0.85rem',
    border: `1px solid ${BRAND_BORDER}`,
    background: '#ffffff',
    color: BRAND_TEXT,
    padding: '0.8rem 0.9rem',
    resize: 'vertical',
    boxSizing: 'border-box'
  },
  dialogError: {
    color: BRAND_DANGER_TEXT,
    fontWeight: 700
  },
  dialogActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.75rem',
    flexWrap: 'wrap'
  },
  emptyState: {
    border: `1px dashed ${BRAND_TEXT_SOFT_BORDER}`,
    borderRadius: '0.9rem',
    padding: '1rem',
    background: BRAND_PANEL,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.4rem'
  },
  emptyTitle: {
    fontWeight: 800,
    color: BRAND_TEXT
  },
  emptyCopy: {
    color: BRAND_MUTED,
    lineHeight: 1.45
  },
  muted: {
    color: BRAND_MUTED,
    lineHeight: 1.45
  },
  previewBackdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(18, 63, 67, 0.78)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
    zIndex: 1100
  },
  previewCard: {
    width: 'min(1020px, 100%)',
    maxHeight: '90vh',
    background: BRAND_CARD,
    borderRadius: '1rem',
    border: `1px solid ${BRAND_BORDER}`,
    padding: '1rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.9rem'
  },
  previewHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '1rem'
  },
  previewTitle: {
    fontWeight: 800,
    fontSize: '1.35rem',
    color: BRAND_TEXT
  },
  previewCount: {
    color: BRAND_MUTED,
    fontSize: '0.9rem'
  },
  previewCloseButton: {
    border: `1px solid ${BRAND_BORDER}`,
    background: '#FFFFFF',
    color: BRAND_TEXT,
    borderRadius: '0.75rem',
    minWidth: '2.7rem',
    minHeight: '2.7rem',
    fontWeight: 800,
    cursor: 'pointer'
  },
  previewToolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.65rem',
    flexWrap: 'wrap'
  },
  previewToolbarButton: {
    border: `1px solid ${BRAND_BORDER}`,
    background: '#FFFFFF',
    color: BRAND_TEXT,
    borderRadius: '0.75rem',
    minHeight: '2.5rem',
    padding: '0.55rem 0.8rem',
    fontWeight: 700,
    cursor: 'pointer'
  },
  previewRange: {
    width: '14rem'
  },
  previewZoomLabel: {
    color: BRAND_MUTED,
    fontWeight: 700
  },
  previewViewport: {
    borderRadius: '0.95rem',
    background: BRAND_PANEL,
    padding: '1rem',
    overflow: 'auto',
    display: 'flex',
    justifyContent: 'center'
  },
  previewImage: {
    width: 'auto',
    height: 'auto',
    objectFit: 'contain',
    display: 'block'
  },
  previewImagePlaceholder: {
    minHeight: '18rem',
    minWidth: '18rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: BRAND_MUTED
  }
};

export default SafetyUpgradeImplementationTask;
